import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import { useTheme, getColors } from "../ThemeContext";
import { useAIAdvice } from "../useAIAdvice";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
  ScatterChart, Scatter,
  LineChart, Line, ReferenceLine, AreaChart, Area, ComposedChart,
} from "recharts";

import { API } from "../config/api";
import PlotSummary from "../components/PlotSummary";

const C = {
  bg: "#050b14", panel: "#080f1e", card: "#0b1525",
  border: "#0f2040", border2: "#162a4a",
  cyan: "#00d4ff", teal: "#00ffcc", darkCyan: "#0b647d", darkIndigo: "#4a4e8d",
  pink: "#afeeee", purple: "#8b7eff", blue: "#3b9eff", amber: "#ffb84d",
  red: "#ff3d5a", green: "#00cc99", orange: "#ff7c2a",
  lime: "#b0ff4a", text: "#e2eaf8", muted: "#4a6080", muted2: "#2a3f60",
};
function applyTheme(colors) {
  const { bg, panel, card, border, border2, text, muted, muted2,
          cyan, teal, amber, pink, purple, blue, red, green, orange, lime } = colors;
  Object.assign(C, { bg, panel, card, border, border2, text, muted, muted2,
                     cyan, teal, amber, purple, blue, red, green, orange, lime });
}

const MONO = "'Space Mono', monospace";

/* Axis titles for the benchmarking plots. Spread onto <XAxis>/<YAxis>; call during render so the current theme colour is used. */
const AXIS_TITLE_FONT = 9, X_AXIS_HEIGHT = 46, Y_AXIS_WIDTH = 74;
const xTitle = value => ({ height: X_AXIS_HEIGHT, label: { value, position: "insideBottom", offset: -2, fill: C.muted, fontSize: AXIS_TITLE_FONT, fontFamily: MONO } });
const yTitle = (value, width = Y_AXIS_WIDTH) => ({ width, label: { value, angle: -90, position: "insideLeft", offset: 4, style: { textAnchor: "middle", fill: C.muted, fontSize: AXIS_TITLE_FONT, fontFamily: MONO } } });
/* ───────────── TIME-WINDOW HELPERS (same bar / axis / look as Overview, AI Controller, GLR) ───────────── */
const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const pad2 = n => String(n).padStart(2, "0");
const HOUR = 3600000, DAY = 86400000;

// backend sends "YYYY-MM-DD HH:MM" - parse as plain local clock time (no timezone shifting)
function parseT(s) {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]).getTime() : null;
}
const fmtDT   = ms => { const d = new Date(ms); return `${d.getDate()} ${MON[d.getMonth()]} ${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const fmtD    = ms => { const d = new Date(ms); return `${d.getDate()} ${MON[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`; };
const fmtHM   = ms => { const d = new Date(ms); return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
const toInput = ms => { const d = new Date(ms); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; };

function fmtSpan(ms) {
  if (ms < HOUR)      return `${Math.max(1, Math.round(ms / 60000))} min`;
  if (ms < 2 * DAY)   return `${Math.round(ms / HOUR)} h`;
  const d = Math.round(ms / DAY);
  return d >= 14 ? `${d} days (${(d / 7).toFixed(1)} wk)` : `${d} days`;
}
function fmtAgo(ms) {
  if (ms < HOUR)    return "just now";
  if (ms < 2 * DAY) return `${Math.round(ms / HOUR)} h ago`;
  return `${Math.round(ms / DAY)} d ago`;
}

const PRESETS = [
  { id: "24h",   label: "24 H",   ms: DAY },
  { id: "7d",    label: "7 D",    ms: 7 * DAY },
  { id: "30d",   label: "30 D",   ms: 30 * DAY },
  { id: "90d",   label: "90 D",   ms: 90 * DAY },
  { id: "cyc30", label: "LAST 30 CYC", cycles: 30 },
  { id: "all",   label: "ALL" },
];
const MIN_POINTS = 3;   // fewer cycles than this cannot draw a meaningful trend

/* Apply a window to ANY array of rows that carry `_t` (ms). Presets are measured back from the LATEST cycle in the data, not from "now". */
function applyWin(w, tMax, arr) {
  if (w.mode === "all" || tMax == null) return arr;
  if (w.mode === "cyc30") {
    if (arr.some(r => r.well != null)) {            // multi-well rows -> newest 30 of EACH well
      const by = {};
      arr.forEach(r => { (by[r.well] = by[r.well] || []).push(r); });
      return Object.values(by).flatMap(a => a.slice(-30));
    }
    return arr.slice(-30);
  }
  if (w.mode === "custom") {
    const f = w.from ? parseT(`${w.from} 00:00`) : -Infinity;
    const t = w.to   ? parseT(`${w.to} 23:59`)   :  Infinity;
    return arr.filter(d => d._t != null && d._t >= f && d._t <= t);
  }
  const p = PRESETS.find(x => x.id === w.mode);
  return arr.filter(d => d._t != null && d._t >= tMax - p.ms);
}
/* `rows` = every cycle time the window bar should know about ({_t, well?}). `filter(arr)` windows any per-well / per-view array the same way. */
function useTimeWindow(rows) {
  const [win, setWin] = useState({ mode: "all", from: "", to: "" });
  const tMin = useMemo(() => { const v = rows.map(d => d._t).filter(t => t != null); return v.length ? Math.min(...v) : null; }, [rows]);
  const tMax = useMemo(() => { const v = rows.map(d => d._t).filter(t => t != null); return v.length ? Math.max(...v) : null; }, [rows]);
  const view = useMemo(() => applyWin(win, tMax, rows), [rows, win, tMax]);
  const presetCounts = useMemo(() => Object.fromEntries(PRESETS.map(p => [p.id, applyWin({ mode: p.id }, tMax, rows).length])), [rows, tMax]);
  return { win, setWin, view, presetCounts, tMin, tMax, isAll: win.mode === "all", filter: arr => applyWin(win, tMax, arr) };
}


/* Second row of the X axis = timestamp of the cycle sitting at that position. */
function DateTick({ x, y, payload, index, lookup, lastIdx, shortSpan }) {
  const t = lookup.get(payload.value);
  if (t == null) return null;
  const anchor = index === 0 ? "start" : index === lastIdx ? "end" : "middle";
  const l1 = shortSpan ? fmtHM(t) : fmtD(t);
  const l2 = shortSpan ? fmtD(t)  : fmtHM(t);
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor={anchor} fill={C.amber} fontSize={9} fontFamily={MONO} dy={11}>{l1}</text>
      <text textAnchor={anchor} fill={C.muted} fontSize={8} fontFamily={MONO} dy={22}>{l2}</text>
    </g>
  );
}

/* Call as a function (NOT <Component/>) - recharts only recognises <XAxis> as a direct child.
   Rows must carry `key` (default "index") and `_t`. labelText = the "Cycle Number →" caption, moved under the timestamp row. */
function dateXAxis(rows, key = "index", nTicks = 5, labelText, opts = {}) {
  const stamped = rows.filter(r => r._t != null);
  if (!stamped.length) return null;
  const last = stamped.length - 1;
  const idx = new Set();
  for (let i = 0; i < nTicks; i++) idx.add(Math.round((i * last) / Math.max(nTicks - 1, 1)));
  const ticks = [...idx].filter(i => stamped[i]).map(i => stamped[i][key]);
  const lookup = new Map(stamped.map(r => [r[key], r._t]));
  const span = stamped.length > 1 ? (stamped[last]._t - stamped[0]._t) : 0;
  return (
    <XAxis xAxisId="date" dataKey={key} ticks={ticks} interval={0} tickLine={false} axisLine={false} height={opts.height || 32}
      tick={<DateTick lookup={lookup} lastIdx={ticks.length - 1} shortSpan={span > 0 && span < 3 * DAY} />}
      label={labelText ? { value: labelText, position: "insideBottom", offset: opts.labelOffset ?? -14, fill: C.muted, fontSize: 9, fontFamily: MONO } : undefined} />
  );
}
const hasStamps = rows => rows.some(r => r._t != null);

function DataWindowBar({ ctl, total, note, isDark }) {
  const { win, setWin, view, presetCounts, tMin, tMax, isAll } = ctl;
  const vs      = view.filter(d => d._t != null);
  const vStart  = vs.length ? Math.min(...vs.map(d => d._t)) : null;
  const vEnd    = vs.length ? Math.max(...vs.map(d => d._t)) : null;
  const hasTime = vStart != null && vEnd != null;
  const spanMs  = hasTime ? Math.max(vEnd - vStart, 0) : 0;
  const tooFew  = view.length < MIN_POINTS;
  const latestAgo = tMax != null ? Date.now() - tMax : null;
  const btn = (active, disabled) => ({
    fontFamily: MONO, fontSize: 10, letterSpacing: 1, padding: "5px 11px", borderRadius: 6, cursor: disabled ? "not-allowed" : "pointer",
    background: active ? `${C.amber}22` : "transparent", color: disabled ? C.muted2 : active ? C.amber : C.muted,
    border: `1px solid ${active ? C.amber : C.border2}`, opacity: disabled ? 0.55 : 1, whiteSpace: "nowrap",
  });
  const dateInput = { fontFamily: MONO, fontSize: 10, background: C.panel, color: C.text, border: `1px solid ${C.border2}`, borderRadius: 6, padding: "4px 8px", colorScheme: isDark ? "dark" : "light" };

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.amber}`, borderRadius: 12, padding: "14px 18px" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 2, color: C.muted }}>DATA WINDOW</span>
        <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.text }}>
          {hasTime ? `${fmtDT(vStart)}  →  ${fmtDT(vEnd)}` : "no timestamps available"}
        </span>
        {hasTime && <span style={{ fontFamily: MONO, fontSize: 10, color: C.amber, background: `${C.amber}14`, border: `1px solid ${C.amber}33`, borderRadius: 5, padding: "2px 8px" }}>
          {isAll ? "CUMULATIVE" : "WINDOW"} · {fmtSpan(spanMs)}
        </span>}
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.cyan, background: `${C.cyan}12`, border: `1px solid ${C.cyan}33`, borderRadius: 5, padding: "2px 8px" }}>
          {view.length} of {total} cycles
        </span>
        {latestAgo != null && (
          <span title="Presets are measured back from the newest cycle in the data, not from today's date"
            style={{ fontFamily: MONO, fontSize: 10, color: latestAgo > DAY ? C.orange : C.green, background: `${latestAgo > DAY ? C.orange : C.green}12`, border: `1px solid ${latestAgo > DAY ? C.orange : C.green}33`, borderRadius: 5, padding: "2px 8px" }}>
            Latest cycle {fmtDT(tMax)} · {fmtAgo(latestAgo)}
          </span>
        )}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        {PRESETS.map(p => {
          const n = presetCounts[p.id];
          const disabled = p.id !== "all" && n < MIN_POINTS;
          return (
            <button key={p.id} disabled={disabled} onClick={() => setWin({ mode: p.id, from: "", to: "" })}
              title={disabled ? `Only ${n} cycle${n === 1 ? "" : "s"} in this window, too few to plot a trend. Try a wider window.` : `${n} cycles`}
              style={btn(win.mode === p.id, disabled)}>
              {p.label} <span style={{ opacity: 0.7 }}>· {n}</span>
            </button>
          );
        })}
        <button onClick={() => setWin(w => ({ mode: "custom", from: w.from || (tMin != null ? toInput(tMin) : ""), to: w.to || (tMax != null ? toInput(tMax) : "") }))}
          style={btn(win.mode === "custom", false)}>CUSTOM RANGE</button>
        {win.mode === "custom" && (
          <>
            <input type="date" value={win.from} min={tMin != null ? toInput(tMin) : undefined} max={win.to || (tMax != null ? toInput(tMax) : undefined)}
              onChange={e => setWin(w => ({ ...w, from: e.target.value }))} style={dateInput} />
            <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>to</span>
            <input type="date" value={win.to} min={win.from || (tMin != null ? toInput(tMin) : undefined)} max={tMax != null ? toInput(tMax) : undefined}
              onChange={e => setWin(w => ({ ...w, to: e.target.value }))} style={dateInput} />
          </>
        )}
      </div>
      {tooFew && (
        <div style={{ fontFamily: MONO, fontSize: 10, color: C.orange, marginTop: 8 }}>
          ⚠ Only {view.length} cycle{view.length === 1 ? "" : "s"} in this range, widen the window to see a trend.
        </div>
      )}
    </div>
  );
}


/* Benchmarking windows server-side: the DATA WINDOW bar becomes from/to/last query params (no params = unchanged) */
function benchWinQs(win, tMax) {
  if (win.mode === "all" || tMax == null) return "";
  if (win.mode === "cyc30") return "last=30";               // newest 30 cycles of EACH well
  if (win.mode === "custom") {
    const p = [];
    if (win.from) p.push(`from=${encodeURIComponent(win.from + " 00:00")}`);
    if (win.to)   p.push(`to=${encodeURIComponent(win.to + " 23:59")}`);
    return p.join("&");
  }
  const pr = PRESETS.find(x => x.id === win.mode);
  return `from=${encodeURIComponent(`${toInput(tMax - pr.ms)} ${fmtHM(tMax - pr.ms)}`)}&to=${encodeURIComponent(`${toInput(tMax)} ${fmtHM(tMax)}`)}`;
}
const withQs = (url, qs) => qs ? `${url}${url.includes("?") ? "&" : "?"}${qs}` : url;
const SANS = "'Syne', sans-serif";
const WELL_ORDER = ["WELL_1", "ABBOTT_9_10_9_W01", "ABBOTT_9_10_9_W02", "ABBOTT_9_10_9_W03", "ABBOTT_9_10_9_W04", "ABBOTT_9_10_9_W05"];
const WELL_COLORS = [C.cyan, C.teal, C.purple, C.darkCyan, C.pink, C.blue];
function shortName(n) {
  if (!n) return "Unknown";
  const parts = n.split("/");
  const last = parts[parts.length - 1];
  if (last === "WELL_1") return "WELL_1";
  return last.replace("ABBOTT_9_10_9_", "ABBOTT_");
}
function sortWells(wells) {
  return [...wells].sort((a, b) => {
    const aName = (a.well_name || a.short || "").includes("WELL_1") ? 0 : 1;
    const bName = (b.well_name || b.short || "").includes("WELL_1") ? 0 : 1;
    return aName - bName;
  });
}

function AnimNum({ to, dec = 1, suffix = "", prefix = "" }) {
  const [val, setVal] = useState(null);
  const ref = useRef();
  useEffect(() => {
    if (to == null || isNaN(Number(to))) { setVal(null); return; }
    const end = Number(to);
    const start = performance.now();
    function tick(now) {
      const p = Math.min((now - start) / 1100, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setVal(e * end);
      if (p < 1) ref.current = requestAnimationFrame(tick);
    }
    ref.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(ref.current);
  }, [to]);
  if (val === null) return <span>-</span>;
  return <span>{prefix}{val.toFixed(dec)}{suffix}</span>;
}

function KPICard({ label, value, dec = 1, suffix = "", color, icon, sub, delay = 0, prefix = "" }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderTop: `3px solid ${color}`,
      borderRadius: 12, padding: "18px 20px", animation: `fade-up 0.5s ease ${delay}s both`,
      position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", top: -30, right: -30, width: 90, height: 90, borderRadius: "50%", background: `${color}10`, pointerEvents: "none" }} />
      <div style={{ fontSize: 18, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily: MONO, fontSize: 18, fontWeight: 700, color, lineHeight: 1 }}>
        <AnimNum to={value} dec={dec} suffix={suffix} prefix={prefix} />
      </div>
      {sub && <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

/* Chart Card */
function ChartCard({ title, subtitle, children, height = 260, accentColor = C.cyan, delay = 0, style = {}, summaryTitle, summarySubtitle, summaryChart, lensId, multiWell = true }) {
  // Summary text: most charts on this page compare wells (one bar per well), so say so unless it is a single-well plot
  const sumSub = [summarySubtitle || (typeof subtitle === "string" ? subtitle : ""), multiWell ? "Compared across wells: one bar per well." : ""].filter(Boolean).join(" ");
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 14,
      padding: "20px 16px 20px", animation: `fade-up 0.5s ease ${delay}s both`,
      position: "relative", overflow: "hidden", ...style,
    }}>
      <div style={{ position: "absolute", top: 0, left: 20, right: 20, height: 2, background: `linear-gradient(to right, transparent, ${accentColor}88, transparent)` }} />
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text }}>{title}</div>
        {subtitle && <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 2 }}>{subtitle}</div>}
      </div>
      <div style={{ height }}>{children}</div>
      <div style={{ marginTop: 10 }}>
        <PlotSummary inline lensId={lensId} title={summaryTitle || title} subtitle={sumSub} accent={accentColor}>{summaryChart || children}</PlotSummary>
      </div>
    </div>
  );
}

/* Section header*/
function SectionHeader({ label, color = C.cyan, sub }) {
  return (
    <div style={{ marginBottom: 18, marginTop: 8 }}>
      <div style={{ 
        fontFamily: SANS, 
        fontWeight: 700, 
        fontSize: 16, 
        color: C.text, 
        letterSpacing: 0.5 
      }}>
        {label}
      </div>
      {sub && <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

/* Sub-tab bar */
function SubTabs({ tabs, active, onChange }) {
  return (
    <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 24 }}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)} style={{
          padding: "7px 16px", borderRadius: 8,
          border: `1px solid ${active === t.id ? (t.color || C.cyan) : C.border2}`,
          background: active === t.id ? `${t.color || C.cyan}18` : C.panel,
          color: active === t.id ? (t.color || C.cyan) : C.muted,
          fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
          cursor: "pointer", transition: "all 0.2s",
          display: "flex", alignItems: "center", gap: 7,
        }}>
          {t.icon}{t.label}
        </button>
      ))}
    </div>
  );
}

/* SVG Icons */
const Icons = {
  well:      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="7" ry="2.5"/><path d="M5 5v14c0 1.38 3.13 2.5 7 2.5s7-1.12 7-2.5V5"/><path d="M5 12c0 1.38 3.13 2.5 7 2.5s7-1.12 7-2.5"/></svg>,
  warning:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  critical:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  ok:        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  trophy:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="8 2 4 6 4 8c0 3.31 3.58 6 8 6s8-2.69 8-6V6l-4-4z"/><line x1="12" y1="14" x2="12" y2="17"/><line x1="8" y1="21" x2="16" y2="21"/></svg>,
  velocity:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  clock:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  droplet:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>,
  gauge:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 1 0 10 10"/><path d="M12 6v6l4 2"/></svg>,
  chart:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  heart:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
  brain:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96-.46 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.44-2.14A2.5 2.5 0 0 1 9.5 2z"/><path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96-.46 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.44-2.14A2.5 2.5 0 0 0 14.5 2z"/></svg>,
  table:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="9" x2="9" y2="21"/></svg>,
  pressure:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>,
  compare:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/></svg>,
  predict:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  physics:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg>,
};

/* AI Tooltip wrapper, same strategy as Overview page TTip (element-form content, debounced fetch, show/hide with chart hover) */
function AITooltip({ active, payload, label, fieldKey, contextData = {}, highContrast = false }) {
  const { advice, loading, fetchAdvice, clearAdvice } = useAIAdvice();
  const prevKey = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const hasData = !!(active && payload?.length);
  const baseContext = hasData
    ? (typeof contextData === "function" ? (contextData({ label, payload }) || {}) : (contextData || {}))
    : {};

  const allFields = hasData
    ? payload
        .filter(p => p.value != null && typeof p.value === "number")
        .map(p => ({
          field: p.dataKey || p.name || fieldKey || "",
          label: p.name || p.dataKey || fieldKey || "",
          value: Math.round(p.value * 100) / 100,
          unit: p.unit || "",
        }))
    : [];
  const primaryP = hasData ? payload[0] : null;
  const primaryVal = primaryP && typeof primaryP.value === "number" ? primaryP.value : null;
  const primaryUnit = primaryP?.unit || "";
  const primaryField = fieldKey || primaryP?.dataKey || "";

  useEffect(() => {
    if (!hasData || primaryVal == null) return;
    const allValsKey = allFields.map(f => `${f.field}:${f.value}`).join("|");
    const key = `${primaryField}_${Math.round(primaryVal * 10) / 10}_${allValsKey}`;
    if (prevKey.current === key) return;
    const timer = setTimeout(() => {
      prevKey.current = key;
      const context = { ...baseContext };
      allFields.forEach(f => { context[f.field] = f.value; });
      fetchAdvice(primaryField, primaryVal, primaryUnit, label, context, allFields);
    }, 400);
    return () => clearTimeout(timer);
  }, [hasData, primaryField, primaryVal, label]);

  useEffect(() => { if (!active) clearAdvice(); }, [active]);

  useEffect(() => {
    function onMove(e) {
      const TW = 340, TH = 300;
      const x = e.clientX + 18, y = e.clientY - 10;
      setPos({
        x: x + TW > window.innerWidth ? e.clientX - TW - 18 : x,
        y: y + TH > window.innerHeight ? window.innerHeight - TH - 16 : y,
      });
    }
    if (active) window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [active]);

  if (!hasData) return null;

  const adviceColor = !advice ? C.muted
    : advice.startsWith("■") ? C.red
    : advice.startsWith("▲") || advice.startsWith("▶") ? C.amber
    : advice.startsWith("▽") ? C.red
    : C.green;

  return ReactDOM.createPortal(
    <div style={{ position: "fixed", left: pos.x, top: pos.y, zIndex: 999999, pointerEvents: "none" }}>
      <div style={{
        background: C.card,
        border: `1px solid ${adviceColor}44`,
        borderLeft: `4px solid ${adviceColor}`,
        borderRadius: 12,
        padding: "12px 16px",
        fontFamily: MONO,
        fontSize: 11,
        boxShadow: "0 20px 50px rgba(0,0,0,0.7)",
        width: 340,
        maxHeight: 420,
        overflowY: "auto",
      }}>
        <div style={{ color: C.cyan, fontWeight: 700, marginBottom: 8, fontSize: 12 }}>
          {label}
        </div>

        {payload.map((p, i) => (
          <div key={i} style={{
            display: "flex", justifyContent: "space-between",
            gap: 16, marginBottom: 6, color: highContrast ? C.text : C.muted,
          }}>
            <span style={{ fontSize: 10 }}>{p.name || p.dataKey}</span>
            <span style={{ color: p.color || C.cyan, fontWeight: 700, fontSize: 12 }}>
              {typeof p.value === "number" ? p.value.toFixed(2) : p.value}
              {p.unit && <span style={{ color: C.muted, fontSize: 9, marginLeft: 4 }}>{p.unit}</span>}
            </span>
          </div>
        ))}

        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10, marginTop: 10 }}>
          {loading ? (
            <div style={{ color: C.muted, fontSize: 10 }}>AI Analyzing...</div>
          ) : advice ? (
            <div style={{ color: adviceColor, lineHeight: 1.6, fontSize: 10.5, wordWrap: "break-word" }}>
              {renderAdvice(advice)}
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}
/* Simple tooltip */
function SimpleTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border2}`, borderRadius: 10, padding: "10px 14px", fontFamily: MONO, fontSize: 11, boxShadow: "0 12px 40px #000c", minWidth: 160 }}>
      <div style={{ color: C.cyan, fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 16, color: C.muted }}>
          <span>{p.name}</span>
          <span style={{ color: p.color || C.cyan, fontWeight: 700 }}>{typeof p.value === "number" ? p.value.toFixed(2) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

/* Velocity zone badge */
function VelZone({ v }) {
  if (v == null) return <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>-</span>;
  const [bg, label] =
    v < 350  ? [C.red,   "TOO SLOW"] :
    v > 1200 ? [C.amber, "TOO FAST"] :
               [C.green, "OPTIMAL"];
  return (
    <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, color: bg, background: `${bg}18`, border: `1px solid ${bg}44`, borderRadius: 4, padding: "2px 7px" }}>
      {label}
    </span>
  );
}

/* Alert severity badge*/
function AlertBadge({ level }) {
  const map = {
    critical: [C.red,   "CRITICAL"],
    warning:  [C.amber, "WARNING"],
    ok:       [C.green, "OK"],
    info:     [C.blue,  "INFO"],
  };
  const [color, label] = map[level] || [C.muted, level?.toUpperCase() || "-"];
  return (
    <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, color, background: `${color}18`, border: `1px solid ${color}44`, borderRadius: 4, padding: "2px 8px" }}>
      {label}
    </span>
  );
}

/* Rank badge*/
function RankBadge({ rank, total }) {
  const color = rank === 1 ? C.amber : rank <= 2 ? C.cyan : rank >= total ? C.red : rank >= total - 1 ? C.orange : C.muted;
  const label = rank === 1 ? "1st" : rank === 2 ? "2nd" : rank === 3 ? "3rd" : `#${rank}`;
  return (
    <span style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, color, background: `${color}15`, border: `1px solid ${color}44`, borderRadius: 6, padding: "3px 10px" }}>
      {label}
    </span>
  );
}

/* AI Panel */
function renderAdvice(text) {
  if (!text) return null;
  const ICONS = {
    "▲": <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><path d="M6.5 1.5L11.8 10.5H1.2L6.5 1.5Z" stroke="#f59e0b" strokeWidth="1.4" fill="#f59e0b22" strokeLinejoin="round" /><line x1="6.5" y1="4.5" x2="6.5" y2="7.5" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round" /><circle cx="6.5" cy="9.2" r="0.8" fill="#f59e0b" /></svg>,
    "■": <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="6.5" cy="6.5" r="5" stroke="#ef4444" strokeWidth="1.4" fill="#ef444422" /><path d="M4.5 4.5L8.5 8.5M8.5 4.5L4.5 8.5" stroke="#ef4444" strokeWidth="1.4" strokeLinecap="round" /></svg>,
    "◆": <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="6.5" cy="6.5" r="5" stroke="#22c55e" strokeWidth="1.4" fill="#22c55e22" /><path d="M4 6.5L5.8 8.3L9 5" stroke="#22c55e" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>,
    "▽": <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><path d="M6.5 2.5V10M3.5 7L6.5 10L9.5 7" stroke="#f87171" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>,
    "▶": <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0, marginTop: 1 }}><path d="M6.5 10.5V2.5M3.5 5.5L6.5 2.5L9.5 5.5" stroke="#f59e0b" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  };
  for (const [sym, icon] of Object.entries(ICONS)) {
    if (text.startsWith(sym + " ")) {
      return <span style={{ display: "flex", alignItems: "flex-start", gap: 6, lineHeight: 1.5 }}>{icon}<span>{text.slice(sym.length + 1)}</span></span>;
    }
  }
  return <span>{text}</span>;
}

function computeHealth(w) {
  const velScore = w.avg_velocity == null
    ? 20
    : w.avg_velocity >= 350 && w.avg_velocity <= 1200 ? 40
    : w.avg_velocity > 200 ? 20 : 0;
  const hasEnoughCycles = (w.total_cycles ?? 0) >= 8;
  const arrScore = w.arrival_rate == null
    ? 10
    : (w.arrival_rate === 0 && !hasEnoughCycles)
      ? 10
      : Math.round((w.arrival_rate / 100) * 20);
  const warnScore = w.total_cycles > 0
    ? Math.max(0, Math.round(25 * (1 - (w.total_warnings ?? 0) / w.total_cycles)))
    : 12;
  const shutScore = w.avg_shutin == null
    ? 8
    : w.avg_shutin >= 30 && w.avg_shutin <= 300 ? 15 : 5;

  return Math.min(100, Math.round(velScore + arrScore + warnScore + shutScore));
}

function deriveAlerts(w) {
  const alerts = [];

  // Velocity checks
  if (w.avg_velocity != null) {
    if (w.avg_velocity > 1200)
      alerts.push({ level: "warning",  msg: `Avg velocity ${w.avg_velocity.toFixed(0)} ft/min exceeds 1200, plunger wear risk`, category: "Velocity" });
    else if (w.avg_velocity < 350)
      alerts.push({ level: "critical", msg: `Avg velocity ${w.avg_velocity.toFixed(0)} ft/min below 350, liquid loading likely`, category: "Velocity" });
    else
      alerts.push({ level: "ok",       msg: `Avg velocity ${w.avg_velocity.toFixed(0)} ft/min, within optimal 350, 1200 ft/min range`, category: "Velocity" });
  }

  if (w.high_velocity_warnings > 0)
    alerts.push({ level: "warning",  msg: `${w.high_velocity_warnings} high-velocity cycles (>1200 ft/min) detected`, category: "Velocity" });

  if (w.low_velocity_warnings > 0) {
    const sev = w.low_velocity_warnings > 10 ? "critical" : "warning";
    alerts.push({ level: sev, msg: `${w.low_velocity_warnings} low-velocity cycles (<350 ft/min), ${sev === "critical" ? "immediate review needed" : "monitor closely"}`, category: "Velocity" });
  }

  // Shut-in checks
  if (w.avg_shutin != null) {
    if (w.avg_shutin > 300)
      alerts.push({ level: "warning",  msg: `Avg shut-in ${w.avg_shutin.toFixed(0)} min exceeds 300, possibly over-closed`, category: "Shut-In" });
    else if (w.avg_shutin < 30)
      alerts.push({ level: "critical", msg: `Avg shut-in ${w.avg_shutin.toFixed(0)} min below 30, insufficient buildup`, category: "Shut-In" });
    else
      alerts.push({ level: "ok",       msg: `Avg shut-in ${w.avg_shutin.toFixed(0)} min, within optimal 30, 300 min range`, category: "Shut-In" });
  }

  // Arrival rate checks
  if ((w.arrival_rate ?? 0) < 50)
    alerts.push({ level: "critical", msg: `Arrival rate ${(w.arrival_rate ?? 0).toFixed(1)}% below 50%, plunger may be stuck or delayed`, category: "Arrival" });
  else if ((w.arrival_rate ?? 0) < 80)
    alerts.push({ level: "warning",  msg: `Arrival rate ${(w.arrival_rate ?? 0).toFixed(1)}% below target 80%`, category: "Arrival" });
  else
    alerts.push({ level: "ok",       msg: `Arrival rate ${(w.arrival_rate ?? 0).toFixed(1)}%`, category: "Arrival" });

  // Health check
  const health = computeHealth(w);
  if (health < 40)
    alerts.push({ level: "critical", msg: `Well health score ${health}/100, critical intervention recommended`, category: "Health" });
  else if (health < 65)
    alerts.push({ level: "warning",  msg: `Well health score ${health}/100, review operational parameters`, category: "Health" });
  else
    alerts.push({ level: "ok",       msg: `Well health score ${health}/100, operating within acceptable range`, category: "Health" });

  return alerts;
}

/* TAB: OVERVIEW*/
function OverviewTab({ wellsShort, summary, totalCycles, aiLoading, advice }) {
  const ranked = [...wellsShort].map(w => ({ ...w, health: computeHealth(w) })).sort((a, b) => b.health - a.health);
  const top3    = ranked.slice(0, 3);
  const bottom3 = ranked.slice(-3).reverse();
  const allAlerts = wellsShort.flatMap(w => deriveAlerts(w).map(a => ({ ...a, well: w.short })));
  const critCount = allAlerts.filter(a => a.level === "critical").length;
  const warnCount = allAlerts.filter(a => a.level === "warning").length;

  const total = ranked.length;
  const statusCounts = {
    healthy:  ranked.filter(w => w.health >= 75).length,
    warning:  ranked.filter(w => w.health >= 50 && w.health < 75).length,
    critical: ranked.filter(w => w.health < 50).length,
    topScore: total > 0 ? ranked[0].health : 0,
    avgScore: total > 0 ? Math.round(ranked.reduce((s, w) => s + w.health, 0) / total) : 0,
  };
  const alertCritCount = allAlerts.filter(a => a.level === "critical").length;
  const alertWarnCount = allAlerts.filter(a => a.level === "warning").length;
  const heatData = wellsShort.map(w => ({
    name: w.short,
    color: w.color,
    health: computeHealth(w),
    arrival: w.arrival_rate ?? 0,
    velOk: w.avg_velocity >= 350 && w.avg_velocity <= 1200 ? 100 :
           w.avg_velocity > 200 ? 50 : 0,
    shutOk: w.avg_shutin >= 30 && w.avg_shutin <= 300 ? 100 : 40,
    warnOk: w.total_cycles > 0 ? Math.max(0, 100 - (w.total_warnings / w.total_cycles) * 100) : 0,
  }));

  /* Arrival pie */
  const arrivalPie = wellsShort.map(w => ({
    name: w.short,
    value: w.arrival_rate ?? 0,
    color: w.color,
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <SectionHeader label="Fleet Overview & Status" color={C.cyan} sub={`${wellsShort.length} wells, ${totalCycles.toLocaleString()} total cycles`} />

      {/* Alert banner */}
      {(critCount > 0 || warnCount > 0) && (
        <div style={{
          background: critCount > 0 ? `${C.red}0d` : `${C.amber}0d`,
          border: `1px solid ${critCount > 0 ? C.red : C.amber}44`,
          borderLeft: `4px solid ${critCount > 0 ? C.red : C.amber}`,
          borderRadius: 10, padding: "14px 20px",
          display: "flex", alignItems: "center", gap: 16,
          animation: "fade-up 0.4s ease both",
        }}>
          <span style={{ color: critCount > 0 ? C.red : C.amber }}>{critCount > 0 ? Icons.critical : Icons.warning}</span>
          <div>
            <div style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: critCount > 0 ? C.red : C.amber, letterSpacing: 0.5 }}>
              FLEET ALERT, {critCount} Critical, {warnCount} Warnings Active
            </div>
            <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 3 }}>
              {critCount > 0 ? "Immediate operator attention required on critical wells." : "Review flagged wells for early intervention."}
            </div>
          </div>
        </div>
      )}

      {/* Top / Bottom performers */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderTop: `3px solid ${C.green}`, borderRadius: 12, padding: "20px 22px", animation: "fade-up 0.4s ease 0.05s both" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <span style={{ color: C.amber }}>{Icons.trophy}</span>
            <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.green }}>Top Performing Wells</div>
          </div>
          <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginBottom: 12, lineHeight: 1.6, background: `${C.green}08`, border: `1px solid ${C.green}22`, borderRadius: 6, padding: "8px 12px" }}>
            <strong style={{ color: C.green }}>What does "vel" mean?</strong> "Vel" = Plunger Arrival Velocity in ft/min. This is how fast the plunger (a small plug inside the pipe) travels from the bottom of the well to the surface.
          </div>
          {top3.map((w, i) => (
            <div key={w.short} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 0", borderBottom: i < top3.length - 1 ? `1px solid ${C.border}` : "none" }}>
              <RankBadge rank={i + 1} total={ranked.length} />
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: MONO, fontSize: 12, color: w.color, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ color: w.color }}>{Icons.well}</span> {w.short}
                </div>
                <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 3 }}>
                  Velocity: {w.avg_velocity?.toFixed(0) ?? "-"} ft/min · Arrival: {w.arrival_rate?.toFixed(1) ?? "-"}%
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: MONO, fontSize: 18, fontWeight: 700, color: C.green }}>{w.health}</div>
                <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>/ 100</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderTop: `3px solid ${C.red}`, borderRadius: 12, padding: "20px 22px", animation: "fade-up 0.4s ease 0.1s both" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <span style={{ color: C.red }}>{Icons.warning}</span>
            <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.red }}>Lowest Performing Wells</div>
          </div>
          <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginBottom: 12, lineHeight: 1.6, background: `${C.red}08`, border: `1px solid ${C.red}22`, borderRadius: 6, padding: "8px 12px" }}>
            These wells have lower health scores and may need operational attention, check velocity, arrival rate, or shut-in duration.
          </div>
          {bottom3.map((w, i) => {
            const critAlerts = deriveAlerts(w).filter(a => a.level === "critical");
            return (
              <div key={w.short} style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 0", borderBottom: i < bottom3.length - 1 ? `1px solid ${C.border}` : "none" }}>
                <RankBadge rank={ranked.length - i} total={ranked.length} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: MONO, fontSize: 12, color: w.color, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ color: w.color }}>{Icons.well}</span> {w.short}
                  </div>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 3 }}>
                    {critAlerts.length > 0 ? (
                      <span style={{ color: C.red }}>{critAlerts.length} critical issue{critAlerts.length > 1 ? "s" : ""}</span>
                    ) : (
                      `Vel: ${w.avg_velocity?.toFixed(0) ?? "-"} · Arr: ${w.arrival_rate?.toFixed(1) ?? "-"}%`
                    )}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontFamily: MONO, fontSize: 18, fontWeight: 700, color: w.health < 50 ? C.red : C.amber }}>{w.health}</div>
                  <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>/ 100</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Fleet Health Multi-Metric Bar Chart & Arrival Pie */}
      <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 20 }}>
          <ChartCard lensId="well-compare"
            title="Fleet Health: Multi-Metric Comparison"
            subtitle="This shows how each well scores based on key health factors"
            accentColor={C.cyan} height={260}
          >
            <ResponsiveContainer>
              <BarChart data={heatData} barCategoryGap="20%">
                <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
                <YAxis domain={[0, 100]} tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...yTitle("Score")} />
                <Tooltip content={<AITooltip fieldKey="WellHealth" contextData={props => ({
                  well_name: props.label,
                  health_metrics: props.payload?.length ? {
                    overall_health: props.payload.find(p => p.dataKey === 'health')?.value,
                    arrival_rate: props.payload.find(p => p.dataKey === 'arrival')?.value,
                    velocity_score: props.payload.find(p => p.dataKey === 'velOk')?.value,
                    warning_free_score: props.payload.find(p => p.dataKey === 'warnOk')?.value,
                  } : {}
                })} />} />
                <Legend formatter={v => <span style={{ fontFamily: MONO, fontSize: 9, color: C.text }}>{v}</span>} />
                <Bar dataKey="health" name="Overall Health" fill={C.green} radius={[4,4,0,0]} />
                <Bar dataKey="arrival" name="Arrival Rate %" fill={C.cyan} radius={[4,4,0,0]} />
                <Bar dataKey="velOk" name="Velocity Score" fill={C.darkIndigo} radius={[4,4,0,0]} />
                <Bar dataKey="warnOk" name="Warning Score" fill={C.purple} radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard lensId="well-compare" summarySubtitle="share of plunger trips that reached the surface in each well (arrival rate %), against the 80% target and 50% warning levels" summaryChart={<BarChart data={arrivalPie}><XAxis dataKey="name" {...xTitle("Well")} /><YAxis label={{ value: "Arrival rate (%)" }} /><ReferenceLine y={80} label={{ value: "80% target" }} /><ReferenceLine y={50} label={{ value: "50% warn" }} /><Bar dataKey="value" name="Arrival Rate %" /></BarChart>}
            title="Plunger Arrival: Fleet Share"
            accentColor={C.teal} height={260}
          >
            <ResponsiveContainer>
              <BarChart
                layout="vertical"
                data={arrivalPie}
                margin={{ top: 4, right: 48, left: 8, bottom: 4 }}
                barCategoryGap="20%"
              >
                <CartesianGrid stroke={C.border} strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} unit="%" {...xTitle("Arrival Rate (%)")} />
                <YAxis type="category" dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...yTitle("Well", 86)} />
                <Tooltip content={<AITooltip fieldKey="PlungerArrived" contextData={props => ({
                  well_name: props.label,
                  arrival_rate: props.payload?.[0]?.value,
                  failure_rate: props.payload?.[0]?.value ? 100 - props.payload[0].value : null,
                  status: props.payload?.[0]?.value >= 80 ? "above_target" : props.payload?.[0]?.value >= 50 ? "below_target" : "critical"
                })} />} />
                <ReferenceLine x={80} stroke={C.green} strokeDasharray="4 2" label={{ value: "80% target", fontFamily: MONO, fontSize: 8, fill: C.green, position: "insideTopRight" }} />
                <ReferenceLine x={50} stroke={C.darkIndigo} strokeDasharray="4 2" label={{ value: "50% warn", fontFamily: MONO, fontSize: 8, fill: C.darkIndigo, position: "insideBottomRight" }} />
                <Bar dataKey="value" name="Arrival Rate %" radius={[0, 4, 4, 0]}
                  label={{ position: "right", fontFamily: MONO, fontSize: 9, fill: C.text, formatter: v => `${v?.toFixed(0)}%` }}>
                  {arrivalPie.map((e, i) => (
                    <Cell
                      key={i}
                      fill={e.color}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

      {/* Plunger Arrived Yes/No per well */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "20px 24px", animation: "fade-up 0.5s ease 0.2s both" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <span style={{ color: C.teal }}>{Icons.gauge}</span>
          <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text }}>Plunger Arrival Status: Per Well (Yes / No)</div>
        </div>
        <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginBottom: 14 }}>
          "Yes" = plunger successfully reached the surface in that cycle. "No" = plunger failed to arrive.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
          {wellsShort.map(w => {
            const yesRate = w.arrival_rate ?? 0;
            const noRate  = 100 - yesRate;
            return (
              <div key={w.short} style={{ background: C.panel, border: `1px solid ${w.color}33`, borderRadius: 10, padding: "14px 16px" }}>
                <div style={{ fontFamily: MONO, fontSize: 11, color: w.color, fontWeight: 700, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                  {Icons.well} {w.short}
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <div style={{ flex: yesRate, background: C.green, borderRadius: 4, height: 8, minWidth: 4, transition: "flex 1s ease" }} title={`Yes: ${yesRate.toFixed(1)}%`} />
                  <div style={{ flex: noRate,  background: C.red,   borderRadius: 4, height: 8, minWidth: 4, transition: "flex 1s ease" }} title={`No: ${noRate.toFixed(1)}%`} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontFamily: MONO, fontSize: 10, color: C.green, fontWeight: 700 }}>YES {yesRate.toFixed(1)}%</span>
                  <span style={{ fontFamily: MONO, fontSize: 10, color: C.red,   fontWeight: 700 }}>NO {noRate.toFixed(1)}%</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Fleet percentage breakdown */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "20px 24px", animation: "fade-up 0.5s ease 0.25s both" }}>
        <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text, marginBottom: 4 }}>Fleet-Wide Percentage Breakdown</div>
        <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginBottom: 18, letterSpacing: 0.5 }}>
          Fleet avg health: <span style={{ color: statusCounts.avgScore >= 75 ? C.green : statusCounts.avgScore >= 50 ? C.amber : C.red, fontWeight: 700 }}>{statusCounts.avgScore}/100</span>
          &nbsp;·&nbsp; Best well: <span style={{ color: statusCounts.topScore >= 75 ? C.green : statusCounts.topScore >= 50 ? C.amber : C.red, fontWeight: 700 }}>{statusCounts.topScore}/100</span>
          &nbsp;·&nbsp; {ranked.length} total wells
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
          {[
            {
              label: "Wells Critical (<50)",
              count: statusCounts.critical,
              pct: total > 0 ? Math.round((statusCounts.critical / total) * 100) : 0,
              color: C.red, icon: Icons.critical,
              note: null,
              showCount: true,
            },
            {
              label: "Critical Alerts (fleet)",
              count: alertCritCount,
              pct: allAlerts.length > 0 ? Math.round((alertCritCount / allAlerts.length) * 100) : 0,
              color: C.red, icon: Icons.critical,
              note: "Matches Alerts tab critical count",
              showCount: true,
            },
            {
              label: "Warning Alerts (fleet)",
              count: alertWarnCount,
              pct: allAlerts.length > 0 ? Math.round((alertWarnCount / allAlerts.length) * 100) : 0,
              color: C.amber, icon: Icons.warning,
              note: "Matches Alerts tab warning count",
              showCount: true,
            },
            {
              label: "Optimal Velocity Cycles",
              count: null,
              pct: summary?.high_velocity_pct != null ? Math.round(100 - summary.high_velocity_pct - summary.low_velocity_pct) : 0,
              color: C.cyan, icon: Icons.velocity,
              note: null,
              showCount: false,
            },
            {
              label: "High-Velocity Cycles",
              count: null,
              pct: summary?.high_velocity_pct ?? 0,
              color: C.orange, icon: Icons.velocity,
              note: null,
              showCount: false,
            },
            {
              label: "Low-Velocity Cycles",
              count: null,
              pct: summary?.low_velocity_pct ?? 0,
              color: C.red, icon: Icons.velocity,
              note: null,
              showCount: false,
            },
          ].map((item, i) => (
            <div key={i} style={{ background: C.panel, borderRadius: 8, padding: "12px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <span style={{ color: item.color }}>{item.icon}</span>
                <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted, letterSpacing: 1 }}>{item.label}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ flex: 1, height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${item.pct}%`, background: item.color, borderRadius: 3, transition: "width 1s ease" }} />
                </div>
                <div style={{ fontFamily: MONO, fontSize: 14, fontWeight: 700, color: item.color, minWidth: 40, textAlign: "right" }}>
                  {item.showCount && item.count != null
                    ? `${item.count}${item.count === 1 ? (item.label.includes("Alert") ? " alert" : " well") : (item.label.includes("Alert") ? " alerts" : " wells")}`
                    : `${item.pct}%`}
                </div>
              </div>
              {item.note && (
                <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginTop: 5 }}>{item.note}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
/* TAB: ALERTS & ALARMS */
function AlertsTab({ wellsShort, winQs }) {
  const [filterLevel, setFilterLevel] = useState("all");
  const [qualityData, setQualityData] = useState(null);
  const [qualityLoading, setQualityLoading] = useState(true);

  useEffect(() => {
    axios.get(withQs(`${API}/portfolio/quality-distribution`, winQs))
      .then(r => { setQualityData(r.data); setQualityLoading(false); })
      .catch(() => setQualityLoading(false));
  }, [winQs]);
  const wellAlerts = wellsShort.map(w => ({
    well: w.short, color: w.color,
    alerts: deriveAlerts(w),
    health: computeHealth(w),
  }));

  wellAlerts.sort((a, b) => {
    const sev = { critical: 0, warning: 1, ok: 2, info: 3 };
    const minA = Math.min(...a.alerts.map(al => sev[al.level] ?? 99));
    const minB = Math.min(...b.alerts.map(al => sev[al.level] ?? 99));
    return minA - minB;
  });

  const allAlerts = wellAlerts.flatMap(w => w.alerts.map(a => ({ ...a, well: w.well, wellColor: w.color })));
  const critCount = allAlerts.filter(a => a.level === "critical").length;
  const warnCount = allAlerts.filter(a => a.level === "warning").length;
  const okCount   = allAlerts.filter(a => a.level === "ok").length;
  const qualityWells   = qualityData?.wells || [];
  const totalExcellent = qualityWells.reduce((s, w) => s + (w.counts?.excellent || 0), 0);
  const totalGood      = qualityWells.reduce((s, w) => s + (w.counts?.good      || 0), 0);
  const totalWarning   = qualityWells.reduce((s, w) => s + (w.counts?.warning   || 0), 0);
  const totalCritical  = qualityWells.reduce((s, w) => s + (w.counts?.critical  || 0), 0);
  const totalScored    = totalExcellent + totalGood + totalWarning + totalCritical;

  const STATUS_META = {
    excellent: { color: C.green,  label: "Excellent", range: "75, 100" },
    good:      { color: C.teal,   label: "Good",      range: "55, 74"  },
    warning:   { color: C.amber,  label: "Warning",   range: "35, 54"  },
    critical:  { color: C.red,    label: "Critical",  range: "0, 34"   },
  };

  const catData = ["Velocity", "Shut-In", "Arrival", "Health", "Status"].map(cat => ({
    cat,
    critical: allAlerts.filter(a => a.category === cat && a.level === "critical").length,
    warning:  allAlerts.filter(a => a.category === cat && a.level === "warning").length,
    ok:       allAlerts.filter(a => a.category === cat && a.level === "ok").length,
  })).filter(d => d.critical + d.warning + d.ok > 0);

  const qualityByWell = {};
  qualityWells.forEach(w => { qualityByWell[w.short] = w; });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <SectionHeader label="Alerts & Alarm Conditions" color={C.red} sub="Cycle-quality scoring + operational threshold alerts across all wells" />

      {/*Quality Distribution*/}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "20px 22px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <div style={{ width: 3, height: 16, background: C.purple, borderRadius: 2 }} />
          <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text }}>
            Cycle Quality Distribution: Per Well
          </div>
        </div>
        <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginBottom: 16 }}>
          factor based rule base scoring (velocity, load factor, plunger arrival, shut-in efficiency)
          {qualityData?.fleet_avg != null && (
            <span>, Fleet avg score: <span style={{ color: qualityData.fleet_avg >= 75 ? C.green : qualityData.fleet_avg >= 55 ? C.teal : qualityData.fleet_avg >= 35 ? C.amber : C.red, fontWeight: 700 }}>{qualityData.fleet_avg}/100</span></span>
          )}
        </div>

        {/* Fleet-wide quality summary badges */}
        {!qualityLoading && totalScored > 0 && (
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
            {[
              { key: "excellent", count: totalExcellent },
              { key: "good",      count: totalGood      },
              { key: "warning",   count: totalWarning   },
              { key: "critical",  count: totalCritical  },
            ].map(({ key, count }) => {
              const m   = STATUS_META[key];
              const pct = totalScored > 0 ? ((count / totalScored) * 100).toFixed(1) : "0.0";
              return (
                <div key={key} style={{
                  background: `${m.color}10`, border: `1px solid ${m.color}33`,
                  borderRadius: 10, padding: "12px 18px", minWidth: 130,
                  display: "flex", flexDirection: "column", gap: 4,
                }}>
                  <div style={{ fontFamily: MONO, fontSize: 9, color: m.color, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 700 }}>
                    {m.label} ({m.range})
                  </div>
                  <div style={{ fontFamily: MONO, fontSize: 22, fontWeight: 700, color: m.color, lineHeight: 1 }}>
                    {count}
                  </div>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>{pct}% of all cycles</div>
                </div>
              );
            })}
            <div style={{
              background: `${C.cyan}08`, border: `1px solid ${C.border}`,
              borderRadius: 10, padding: "12px 18px", minWidth: 130,
              display: "flex", flexDirection: "column", gap: 4,
            }}>
              <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, letterSpacing: 1.2, textTransform: "uppercase" }}>
                Avg Health Score
              </div>
              <div style={{ fontFamily: MONO, fontSize: 22, fontWeight: 700, color: qualityData?.fleet_avg >= 75 ? C.green : qualityData?.fleet_avg >= 55 ? C.teal : qualityData?.fleet_avg >= 35 ? C.amber : C.red, lineHeight: 1 }}>
                {qualityData?.fleet_avg ?? "-"}<span style={{ fontSize: 12, color: C.muted }}>/100</span>
              </div>
              <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>
                {totalCritical > 0 ? `${((totalCritical / totalScored) * 100).toFixed(1)}% critical` : ""}
                {totalExcellent > 0 ? `  ${((totalExcellent / totalScored) * 100).toFixed(1)}% excellent` : ""}
              </div>
            </div>
          </div>
        )}

        {/* Per-well quality distribution rows */}
        {qualityLoading ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 0" }}>
            <div style={{ width: 12, height: 12, border: `2px solid ${C.border2}`, borderTop: `2px solid ${C.purple}`, borderRadius: "50%", animation: "spin-ring 0.8s linear infinite" }} />
            <span style={{ fontFamily: MONO, fontSize: 11, color: C.muted }}>Scoring all cycles…</span>
          </div>
        ) : qualityWells.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <style>{`@keyframes spin-ring { to { transform: rotate(360deg); } }`}</style>
            {qualityWells
              .slice()
              .sort((a, b) => (b.avg_score ?? 0) - (a.avg_score ?? 0))
              .map(w => {
                const wMeta = wellsShort.find(ws => ws.short === w.short) || {};
                const wColor = wMeta.color || C.cyan;
                const scored = w.scored_cycles || 1;
                return (
                  <div key={w.short} style={{
                    background: C.panel, border: `1px solid ${C.border}`,
                    borderRadius: 10, padding: "14px 16px",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                      <div style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: wColor, display: "flex", alignItems: "center", gap: 6 }}>
                        {Icons.well} {w.short}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                        <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>{w.scored_cycles} cycles scored</span>
                        <span style={{ fontFamily: MONO, fontSize: 14, fontWeight: 700,
                          color: w.avg_score >= 75 ? C.green : w.avg_score >= 55 ? C.teal : w.avg_score >= 35 ? C.amber : C.red }}>
                          {w.avg_score ?? "-"}<span style={{ fontSize: 9, color: C.muted }}>/100</span>
                        </span>
                      </div>
                    </div>

                    {/* Legend dots */}
                    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 6 }}>
                      {["excellent","good","warning","critical"].map(key => {
                        const count = w.counts?.[key] ?? 0;
                        const pct   = w.pct?.[key]   ?? 0;
                        if (count === 0) return null;
                        const m = STATUS_META[key];
                        return (
                          <div key={key} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            <div style={{ width: 8, height: 8, borderRadius: 2, background: m.color, flexShrink: 0 }} />
                            <span style={{ fontFamily: MONO, fontSize: 9, color: m.color, fontWeight: 700 }}>
                              {m.label} {pct}%
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Segmented bar */}
                    <div style={{
                      display: "flex", height: 12, borderRadius: 6,
                      overflow: "hidden", background: C.border,
                      gap: 1, marginBottom: 8,
                    }}>
                      {["excellent","good","warning","critical"].map(key => {
                        const pct = w.pct?.[key] ?? 0;
                        if (pct === 0) return null;
                        const m = STATUS_META[key];
                        return (
                          <div
                            key={key}
                            style={{
                              width: `calc(${pct}% - 1px)`,
                              background: m.color,
                              transition: "width 0.8s ease",
                              minWidth: pct > 1 ? 2 : 0,
                            }}
                            title={`${m.label}: ${w.counts?.[key]} cycles (${pct}%)`}
                          />
                        );
                      })}
                    </div>

                    {/* Count pills */}
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {["excellent","good","warning","critical"].map(key => {
                        const count = w.counts?.[key] ?? 0;
                        const pct   = w.pct?.[key]   ?? 0;
                        if (count === 0) return null;
                        const m = STATUS_META[key];
                        return (
                          <span key={key} style={{
                            fontFamily: MONO, fontSize: 9, fontWeight: 700,
                            color: m.color, background: `${m.color}15`,
                            border: `1px solid ${m.color}44`,
                            borderRadius: 4, padding: "2px 8px",
                          }}>
                            {m.label} {count} ({pct}%)
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
          </div>
        ) : (
          <div style={{ fontFamily: MONO, fontSize: 11, color: C.muted }}>No quality data available.</div>
        )}
      </div>

      {/* Operational Threshold Alerts*/}
      <SectionHeader label="Well wise Operational Threshold Alerts" color={C.amber} sub="Velocity, arrival rate, shut-in duration alerts per well" />
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        {[
          {
            level: "all",
            label: "All",
            count: allAlerts.length,
            color: C.cyan,
            icon: Icons.chart,
            tooltip: "All operational alert rule checks across every well",
          },
          {
            level: "critical",
            label: "Critical",
            count: critCount,
            color: C.red,
            icon: Icons.critical,
            tooltip: "Threshold checks that are critical across all wells",
          },
          {
            level: "warning",
            label: "Warning",
            count: warnCount,
            color: C.amber,
            icon: Icons.warning,
            tooltip: "Threshold checks flagged as warnings across all wells",
          },
          {
            level: "ok",
            label: "OK",
            count: okCount,
            color: C.green,
            icon: Icons.ok,
            tooltip: "Threshold checks passing (within normal range) across all wells",
          },
        ].map(f => (
          <button
            key={f.level}
            onClick={() => setFilterLevel(f.level)}
            title={f.tooltip}
            style={{
              padding: "10px 20px", borderRadius: 8,
              border: `1px solid ${filterLevel === f.level ? f.color : C.border2}`,
              background: filterLevel === f.level ? `${f.color}18` : C.panel,
              color: filterLevel === f.level ? f.color : C.muted,
              fontFamily: MONO, fontSize: 11, fontWeight: 700, cursor: "pointer", transition: "all 0.2s",
              display: "flex", alignItems: "center", gap: 8,
            }}
          >
            <span style={{ color: f.color }}>{f.icon}</span>
            <span style={{ fontFamily: MONO, fontSize: 16, fontWeight: 700, color: f.color }}>{f.count}</span>
            <span>{f.label}</span>
          </button>
        ))}
      </div>

      {/* Per-well alert cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 16 }}>
        {wellAlerts.map(({ well, color, alerts, health }) => {
          const visible = filterLevel === "all" ? alerts : alerts.filter(a => a.level === filterLevel);
          if (visible.length === 0) return null;
          const hasCrit   = alerts.some(a => a.level === "critical");
          const hasWarn   = alerts.some(a => a.level === "warning");
          const accentCol = hasCrit ? C.red : hasWarn ? C.amber : C.green;
          const qw = qualityByWell[well];
          return (
            <div key={well} style={{
              background: C.card, border: `1px solid ${accentCol}33`,
              borderTop: `3px solid ${accentCol}`, borderRadius: 12, padding: "16px 18px",
              animation: "fade-up 0.4s ease both",
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700, color, display: "flex", alignItems: "center", gap: 6 }}>
                  {Icons.well} {well}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  {qw?.avg_score != null && (
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, letterSpacing: 1 }}>QUALITY</div>
                      <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700,
                        color: qw.avg_score >= 75 ? C.green : qw.avg_score >= 55 ? C.teal : qw.avg_score >= 35 ? C.amber : C.red }}>
                        {qw.avg_score}/100
                      </div>
                    </div>
                  )}
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, letterSpacing: 1 }}>HEALTH</div>
                    <div style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700,
                      color: health >= 75 ? C.green : health >= 50 ? C.amber : C.red }}>
                      {health}
                    </div>
                  </div>
                </div>
              </div>

              {/* Per-well quality pill summary */}
              {qw && (
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 10 }}>
                  {["excellent","good","warning","critical"].map(key => {
                    const count = qw.counts?.[key] ?? 0;
                    if (count === 0) return null;
                    const m = STATUS_META[key];
                    return (
                      <span key={key} style={{
                        fontFamily: MONO, fontSize: 9, fontWeight: 700,
                        color: m.color, background: `${m.color}15`,
                        border: `1px solid ${m.color}33`,
                        borderRadius: 4, padding: "1px 6px",
                      }}>
                        {m.label} {count}
                      </span>
                    );
                  })}
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {visible.map((al, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "flex-start", gap: 10,
                    background: `${al.level === "critical" ? C.red : al.level === "warning" ? C.amber : al.level === "ok" ? C.green : C.blue}0a`,
                    border: `1px solid ${al.level === "critical" ? C.red : al.level === "warning" ? C.amber : al.level === "ok" ? C.green : C.blue}22`,
                    borderRadius: 8, padding: "10px 12px",
                  }}>
                    <span style={{ color: al.level === "critical" ? C.red : al.level === "warning" ? C.amber : C.green, marginTop: 1 }}>
                      {al.level === "critical" ? Icons.critical : al.level === "warning" ? Icons.warning : Icons.ok}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <AlertBadge level={al.level} />
                        <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted, letterSpacing: 1 }}>{al.category}</span>
                      </div>
                      <div style={{ fontFamily: MONO, fontSize: 10, color: C.text, lineHeight: 1.5 }}>{al.msg}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
/* TAB: PERFORMANCE */
function PerformanceTab({ wellsShort, summary, aiLoading, advice }) {
  const velZoneData = wellsShort.map(w => {
    const total = w.total_cycles;
    const highV = w.high_velocity_warnings;
    const lowV  = w.low_velocity_warnings;
    const optV  = Math.max(0, total - highV - lowV);
    return { name: w.short, Optimal: optV, TooFast: highV, TooSlow: lowV };
  });

  const volumeData = [...wellsShort].sort((a, b) => (b.avg_volume ?? 0) - (a.avg_volume ?? 0));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <SectionHeader label="Performance Analysis: Velocity & Arrival" color={C.amber} sub="Cross-well velocity zones, arrival rates and cycle volumes" />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <ChartCard lensId="well-compare" title="Arrival Rate per Well (%)" subtitle="Higher = more successful plunger trips to surface" accentColor={C.teal} height={240}>
          <ResponsiveContainer>
            <BarChart data={wellsShort} barCategoryGap="30%">
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
              <XAxis dataKey="short" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
              <YAxis domain={[0, 100]} tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} unit="%" {...yTitle("Arrival Rate (%)")} />
              <Tooltip content={<AITooltip fieldKey="ArrivalVelocity" />} />
              <ReferenceLine y={80} stroke={C.green} strokeDasharray="4 2" label={{ value: "80% target", fontFamily: MONO, fontSize: 9, fill: C.green }} />
              <ReferenceLine y={50} stroke={C.darkCyan} strokeDasharray="4 2" label={{ value: "50% warn",   fontFamily: MONO, fontSize: 9, fill: C.darkCyan }} />
              <Bar dataKey="arrival_rate" name="Arrival Rate %" radius={[4, 4, 0, 0]}>
                {wellsShort.map((w, i) => <Cell key={i} fill={w.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard lensId="velocity" title="Average Arrival Velocity (ft/min)" subtitle="Too slow = liquid load. Too fast = energy waste." accentColor={C.amber} height={240}>
          <ResponsiveContainer>
            <BarChart data={wellsShort} barCategoryGap="30%">
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
              <XAxis dataKey="short" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
              <YAxis tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} unit=" ft/m" {...yTitle("Velocity (ft/min)")} />
              <Tooltip content={<AITooltip fieldKey="ArrivalVelocity" />} />
              <ReferenceLine y={1200} stroke={C.darkCyan} strokeDasharray="4 2" label={{ value: "Max 1200", fontFamily: MONO, fontSize: 9, fill: C.darkCyan }} />
              <ReferenceLine y={350}  stroke={C.red}   strokeDasharray="4 2" label={{ value: "Min 350",  fontFamily: MONO, fontSize: 9, fill: C.red }} />
              <Bar dataKey="avg_velocity" name="Avg Velocity (ft/min)" radius={[4, 4, 0, 0]}>
                {wellsShort.map((w, i) => <Cell key={i} fill={w.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <ChartCard title="Velocity Zone Distribution per Well" subtitle="Stacked: Optimal, Too Fast, Too Slow cycle counts" accentColor={C.cyan} height={240}>
        <ResponsiveContainer>
          <BarChart data={velZoneData} barCategoryGap="30%" stackOffset="expand">
            <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
            <YAxis tickFormatter={v => `${Math.round(v * 100)}%`} tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...yTitle("Share of Cycles in Each Zone (%)", 64)} />
            <Tooltip content={<AITooltip fieldKey="ArrivalVelocity" />} />
            <Legend formatter={v => <span style={{ fontFamily: MONO, fontSize: 10, color: C.text }}>{v}</span>} />
            <Bar dataKey="Optimal" name="Optimal (350, 1200 ft/min)" fill={C.green} stackId="a" radius={[0,0,0,0]} />
            <Bar dataKey="TooFast" name="Too Fast (>1200 ft/min)"   fill={C.darkCyan} stackId="a" radius={[0,0,0,0]} />
            <Bar dataKey="TooSlow" name="Too Slow (<350 ft/min)"    fill={C.pink}   stackId="a" radius={[4,4,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <ChartCard title="Avg Gas Volume per Cycle (MCF)" subtitle="Higher = more gas produced per plunger trip" accentColor={C.teal} height={230}>
          <ResponsiveContainer>
            <BarChart data={volumeData} barCategoryGap="35%">
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
              <XAxis dataKey="short" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
              <YAxis tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...yTitle("Avg Volume (MCF)")} />
              <Tooltip content={<AITooltip fieldKey="VolCycleTotal" />} />
              <Bar dataKey="avg_volume" name="Avg Volume (MCF)" radius={[4, 4, 0, 0]}>
                {volumeData.map((w, i) => <Cell key={i} fill={w.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Avg Shut-In Duration (min)" subtitle="Outside the optimal range means re-tune the well." accentColor={C.blue} height={230}>
          <ResponsiveContainer>
            <BarChart data={wellsShort} barCategoryGap="35%">
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
              <XAxis dataKey="short" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
              <YAxis tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} unit=" min" {...yTitle("Shut-In Duration (min)")} />
              <Tooltip content={<AITooltip fieldKey="TimeOffTotal" />} />
              <ReferenceLine y={300} stroke={C.red}   strokeDasharray="4 2" label={{ value: "Max 300", fontFamily: MONO, fontSize: 9, fill: C.red }} />
              <ReferenceLine y={30}  stroke={C.darkCyan} strokeDasharray="4 2" label={{ value: "Min 30",  fontFamily: MONO, fontSize: 9, fill: C.darkCyan }} />
              <Bar dataKey="avg_shutin" name="Avg Shut-In (min)" radius={[4, 4, 0, 0]}>
                {wellsShort.map((w, i) => <Cell key={i} fill={w.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}

/* TAB: PRESSURE ANALYSIS*/
function PressureTab({ wellsShort, pressureData, pressureLoading }) {
  const { advice: aiAdvice, loading: aiLoading, fetchAdvice } = useAIAdvice();
  if (pressureLoading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "40vh" }}>
      <div style={{ fontFamily: MONO, fontSize: 13, color: C.muted }}>Loading pressure data…</div>
    </div>
  );
  const csgData = wellsShort.map(w => {
    const pd = pressureData?.find(p =>
      p.well === w.short ||
      p.well === w.well_name?.split("/").pop() ||
      w.short?.includes(p.well) ||
      p.well?.includes(w.short)
    ) || {};
    return {
      name: w.short, color: w.color,
      csg_open:  pd.avg_csg_open  ?? null,
      csg_close: pd.avg_csg_close ?? null,
      tbg_open:  pd.avg_tbg_open  ?? null,
      tbg_close: pd.avg_tbg_close ?? null,
      line_open: pd.avg_line_open ?? null,
    };
  });
 
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <SectionHeader label="Pressure Analysis: Cross-Well Comparison" color={C.blue} sub="Casing & tubing pressure at open/close events" />
 
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <ChartCard title="Avg Casing Pressure at Open (psi)" subtitle="Higher = more stored energy for lift" accentColor={C.cyan} height={230}>
          <ResponsiveContainer>
            <BarChart data={csgData} barCategoryGap="30%">
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
              <YAxis tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} unit=" psi" {...yTitle("Casing Pressure (psi)")} />
              <Tooltip content={<AITooltip fieldKey="CsgAtOpen" />} />
              <Bar dataKey="csg_open" name="Csg @ Open (psi)" radius={[4, 4, 0, 0]}>
                {csgData.map((w, i) => <Cell key={i} fill={w.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
 
        <ChartCard title="Avg Tubing Pressure at Open (psi)" subtitle="Lower vs casing = differential drives lift" accentColor={C.teal} height={230}>
          <ResponsiveContainer>
            <BarChart data={csgData} barCategoryGap="30%">
              <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
              <YAxis tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} unit=" psi" {...yTitle("Tubing Pressure (psi)")} />
              <Tooltip content={<AITooltip fieldKey="CsgAtOpen" />} />
              <Bar dataKey="tbg_open" name="Tbg @ Open (psi)" radius={[4, 4, 0, 0]}>
                {csgData.map((w, i) => <Cell key={i} fill={w.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
 
      {/* Grouped comparison: open vs close casing */}
      <ChartCard lensId="pressure-open" title="Casing Pressure: Open vs Close Comparison" subtitle="Drop between open and close indicates energy used per cycle" accentColor={C.purple} height={240}>
        <ResponsiveContainer>
          <BarChart data={csgData} barCategoryGap="25%">
            <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
            <YAxis tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} unit=" psi" {...yTitle("Casing Pressure (psi)")} />
            <Tooltip content={<AITooltip fieldKey="CsgAtOpen" />} />
            <Legend formatter={v => <span style={{ fontFamily: MONO, fontSize: 10, color: C.text }}>{v}</span>} />
            <Bar dataKey="csg_open"  name="Csg @ Open"  fill={C.cyan}   radius={[4, 4, 0, 0]} />
            <Bar dataKey="csg_close" name="Csg @ Close" fill={C.purple} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
 
      {/* Pressure summary table */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden", animation: "fade-up 0.5s ease 0.2s both" }}>
        <div style={{ padding: "16px 22px 12px", fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text, borderBottom: `1px solid ${C.border}` }}>
          Pressure Statistics Table
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: MONO, fontSize: 11 }}>
            <thead>
              <tr>
                {["Well", "Csg@Open (psi)", "Csg@Close (psi)", "Tbg@Open (psi)", "ΔCsg (psi)", "Line@Open (psi)"].map(h => (
                  <th 
                    key={h} 
                    style={{ 
                      padding: "12px 16px", 
                      background: C.panel, 
                      color: C.muted, 
                      fontSize: 9, 
                      letterSpacing: 1, 
                      textTransform: "uppercase", 
                      textAlign: h === "Well" ? "left" : "right", 
                      borderBottom: `1px solid ${C.border}`, 
                      whiteSpace: "nowrap" 
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {csgData.map((w, i) => {
                const deltaCsg = w.csg_open != null && w.csg_close != null 
                  ? (w.csg_open - w.csg_close).toFixed(1) 
                  : "-";

                return (
                  <tr 
                    key={i} 
                    style={{ background: i % 2 === 0 ? "transparent" : "#ffffff05" }}
                    onMouseEnter={e => e.currentTarget.style.background = `${C.cyan}0a`}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? "transparent" : "#ffffff05"}
                  >
                    <td style={{ padding: "10px 16px", color: w.color, borderBottom: `1px solid ${C.border}`, fontWeight: 700 }}>
                      {w.name}
                    </td>
                    <td style={{ padding: "10px 16px", color: C.cyan, textAlign: "right", borderBottom: `1px solid ${C.border}` }}>
                      {w.csg_open?.toFixed(1) ?? "-"}
                    </td>
                    <td style={{ padding: "10px 16px", color: C.purple, textAlign: "right", borderBottom: `1px solid ${C.border}` }}>
                      {w.csg_close?.toFixed(1) ?? "-"}
                    </td>
                    <td style={{ padding: "10px 16px", color: C.teal, textAlign: "right", borderBottom: `1px solid ${C.border}` }}>
                      {w.tbg_open?.toFixed(1) ?? "-"}
                    </td>
                    <td style={{ padding: "10px 16px", color: C.amber, textAlign: "right", borderBottom: `1px solid ${C.border}`, fontWeight: 700 }}>
                      {deltaCsg}
                    </td>
                    <td style={{ padding: "10px 16px", color: C.blue, textAlign: "right", borderBottom: `1px solid ${C.border}` }}>
                      {w.line_open?.toFixed(1) ?? "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* TAB: WELL HEALTH*/
function HealthTab({ wellsShort }) {
  const { advice: aiAdvice, loading: aiLoading, fetchAdvice } = useAIAdvice();
  const healthData = [...wellsShort].map(w => ({ ...w, score: computeHealth(w) })).sort((a, b) => b.score - a.score);
  const avgHealth = healthData.length ? Math.round(healthData.reduce((s, w) => s + w.score, 0) / healthData.length) : 0;

  useState(() => {
    if (wellsShort.length) fetchAdvice("WellHealth", avgHealth, "/100", null, { well_count: wellsShort.length });
  });
  const metricsData = wellsShort.map(w => {
    const totalCyc = w.total_cycles || 1;
    const warnPct  = Math.round(Math.max(0, 100 - ((w.total_warnings ?? 0) / totalCyc) * 100));
    return {
      name:        w.short,
      color:       w.color,
      velocity_pct: w.avg_velocity == null ? 0
        : w.avg_velocity >= 350 && w.avg_velocity <= 1200
          ? 100
          : w.avg_velocity > 1200
            ? Math.max(0, Math.round(100 - ((w.avg_velocity - 1200) / 20)))
            : Math.max(0, Math.round((w.avg_velocity / 350) * 100)),
      arrival_rate:  Math.round(w.arrival_rate ?? 0),
      warning_free:  warnPct,
      shutin_pct: w.avg_shutin == null ? 0
        : w.avg_shutin >= 30 && w.avg_shutin <= 300
          ? 100
          : w.avg_shutin < 30
            ? Math.round((w.avg_shutin / 30) * 100)
            : Math.max(0, Math.round(100 - ((w.avg_shutin - 300) / 10))),
      /* Raw values for tooltip */
      _velocity: w.avg_velocity,
      _arrival:  w.arrival_rate,
      _shutin:   w.avg_shutin,
      _warnings: w.total_warnings ?? 0,
      _cycles:   w.total_cycles ?? 0,
      health:    computeHealth(w),
    };
  });
  const metricCtx = ({ label }) => {
    const d = metricsData.find(m => m.name === label);
    if (!d) return {};
    return {
      well_name: label,
      velocity_ft_min: d._velocity,
      velocity_score_pct: d.velocity_pct,
      arrival_rate_pct: d._arrival,
      warning_free_pct: d.warning_free,
      shutin_duration_min: d._shutin,
      shutin_score_pct: d.shutin_pct,
      overall_health_score: d.health,
      optimal_velocity_range: "350-1200 ft/min",
      optimal_shutin_range: "30-300 min",
    };
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <SectionHeader label="Well Health Scoring" color={C.green} sub="Composite score: Velocity + Arrival + Warnings + Shut-In" />

      {/* Health score leaderboard */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "22px 24px", animation: "fade-up 0.4s ease both" }}>
        <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text, marginBottom: 20 }}>Health Score Leaderboard</div>
        {healthData.map((w, i) => {
          const pct   = Math.min(100, w.score);
          const color = pct >= 75 ? C.green : pct >= 50 ? C.amber : C.red;
          const statusLabel = pct >= 75 ? "Healthy" : pct >= 50 ? "Needs Monitoring" : "Critical";
          return (
            <div key={w.short} style={{ marginBottom: i < healthData.length - 1 ? 18 : 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 5 }}>
                <RankBadge rank={i + 1} total={healthData.length} />
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: MONO, fontSize: 12, color: w.color, fontWeight: 700, minWidth: 120 }}>
                  {Icons.well} {w.short}
                </div>
                <div style={{ flex: 1, height: 12, background: C.border, borderRadius: 6, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${pct}%`, background: `linear-gradient(90deg, ${color}88, ${color})`, borderRadius: 6, transition: "width 1.2s ease" }} />
                </div>
                <div style={{ fontFamily: MONO, fontSize: 16, fontWeight: 700, color, minWidth: 60, textAlign: "right" }}>
                  {w.score}<span style={{ fontSize: 10, color: C.muted }}>/100</span>
                </div>
                <span style={{ fontFamily: MONO, fontSize: 9, color, background: `${color}18`, border: `1px solid ${color}44`, borderRadius: 4, padding: "2px 8px" }}>{statusLabel}</span>
              </div>
              <div style={{ marginLeft: 130, fontFamily: MONO, fontSize: 9, color: C.muted2, lineHeight: 1.5 }}>
                Velocity: {w.avg_velocity?.toFixed(0) ?? "-"} ft/min
                &nbsp;·&nbsp;Arrival: {w.arrival_rate?.toFixed(1) ?? "-"}%
                &nbsp;·&nbsp;Shut-In: {w.avg_shutin?.toFixed(0) ?? "-"} min
                &nbsp;·&nbsp;Warnings: {w.total_warnings ?? 0}/{w.total_cycles ?? 0} cycles
              </div>
            </div>
          );
        })}
      </div>
      <ChartCard lensId="well-compare"
        title="Health Metric Scores: Per Well"
        subtitle="Velocity & shut-in optimal range, arrival & warnings percentages"
        accentColor={C.green}
        height={300}
      >
        <ResponsiveContainer>
          <BarChart data={metricsData} barCategoryGap="20%" barGap={3}>
            <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
            <YAxis
              domain={[0, 100]}
              unit="%"
              tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }}
              label={{ value: "Score (0, 100%)", angle: -90, position: "insideLeft", offset: 10, style: { fontFamily: MONO, fontSize: 9, fill: C.muted } }}
            />
            <Tooltip content={<AITooltip fieldKey="WellHealthMetrics" contextData={metricCtx} />} />
            <Legend formatter={v => <span style={{ fontFamily: MONO, fontSize: 9, color: C.text }}>{v}</span>} />
            <ReferenceLine y={80} stroke={C.green} strokeDasharray="4 2"
              label={{ value: "80% good", fontFamily: MONO, fontSize: 8, fill: C.green, position: "insideTopRight" }} />
            <ReferenceLine y={50} stroke={C.amber} strokeDasharray="4 2"
              label={{ value: "50% warn", fontFamily: MONO, fontSize: 8, fill: C.amber, position: "insideBottomRight" }} />
            <Bar dataKey="velocity_pct" name="Velocity score" fill={C.darkCyan} radius={[3,3,0,0]} />
            <Bar dataKey="arrival_rate"  name="Arrival rate %"  fill={C.cyan}    radius={[3,3,0,0]} />
            <Bar dataKey="warning_free"  name="Warning %"  fill={C.purple}  radius={[3,3,0,0]} />
            <Bar dataKey="shutin_pct"    name="Shut-in score"   fill={C.teal}    radius={[3,3,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
      <div style={{
        background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10,
        padding: "14px 18px", display: "flex", gap: 24, flexWrap: "wrap",
      }}>
        {[
          { color: C.darkCyan, label: "Velocity score",   desc: "100% if 350, 1200 ft/min. Scales down outside optimal range." },
          { color: C.cyan,     label: "Arrival rate %",   desc: "Direct % of cycles where plunger reached surface." },
          { color: C.purple,   label: "Warning %",   desc: "% of cycles with no warning flags. 100% = zero warnings." },
          { color: C.teal,     label: "Shut-in score",    desc: "100% if 30, 300 min. Scales down outside optimal range." },
        ].map((item, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, minWidth: 180 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: item.color, flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ fontFamily: MONO, fontSize: 10, color: item.color, fontWeight: 700 }}>{item.label}</div>
              <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginTop: 2, lineHeight: 1.5 }}>{item.desc}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
        {healthData.map(w => {
          const velStatus  = w.avg_velocity >= 350 && w.avg_velocity <= 1200 ? { text: "Optimal", color: C.green } : w.avg_velocity > 1200 ? { text: "Too Fast, reduce shut-in time", color: C.amber } : { text: "Too Slow, increase shut-in time", color: C.red };
          const arrStatus  = (w.arrival_rate ?? 0) >= 80 ? { text: "Above target (≥80%)", color: C.green } : (w.arrival_rate ?? 0) >= 50 ? { text: "Below target, monitor", color: C.amber } : { text: "Critical, check plunger", color: C.red };
          const shutStatus = w.avg_shutin >= 30 && w.avg_shutin <= 300 ? { text: "Optimal (30, 300 min)", color: C.green } : { text: "Outside ideal range", color: C.amber };
          return (
            <div key={w.short} style={{ background: C.card, border: `1px solid ${w.color}33`, borderTop: `3px solid ${w.color}`, borderRadius: 12, padding: "16px 18px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ fontFamily: MONO, fontSize: 12, color: w.color, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                  {Icons.well} {w.short}
                </div>
                <div style={{ fontFamily: MONO, fontSize: 20, fontWeight: 700, color: w.score >= 75 ? C.green : w.score >= 50 ? C.amber : C.red }}>
                  {w.score}<span style={{ fontSize: 10, color: C.muted }}>/100</span>
                </div>
              </div>
              {[
                { icon: Icons.velocity, label: "Velocity", value: `${w.avg_velocity?.toFixed(0) ?? "-"} ft/min`, status: velStatus },
                { icon: Icons.gauge,    label: "Arrival Rate", value: `${w.arrival_rate?.toFixed(1) ?? "-"}%`,   status: arrStatus },
                { icon: Icons.clock,    label: "Avg Shut-In",  value: `${w.avg_shutin?.toFixed(0) ?? "-"} min`, status: shutStatus },
              ].map((m, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 0", borderBottom: i < 2 ? `1px solid ${C.border}` : "none" }}>
                  <span style={{ color: m.status.color, marginTop: 2 }}>{m.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>{m.label}</span>
                      <span style={{ fontFamily: MONO, fontSize: 11, color: m.status.color, fontWeight: 700 }}>{m.value}</span>
                    </div>
                    <div style={{ fontFamily: MONO, fontSize: 9, color: m.status.color, marginTop: 2 }}>{m.status.text}</div>
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
/* TAB: WELL PARAMETERS COMPARISON */

function WellParamsTab({ wellsShort }) {
  const { advice: aiAdvice, loading: aiLoading, fetchAdvice } = useAIAdvice();

  /* Shut-in comparison */
  const shutinData = wellsShort.map(w => ({
    name: w.short,
    color: w.color,
    avg_shutin: w.avg_shutin ?? 0,
  }));

  /* After-flow proxy */
  const afterFlowData = wellsShort.map(w => ({
    name: w.short,
    color: w.color,
    after_flow: w.avg_after_flow ?? w.avg_volume ?? 0,
  }));

  /* Velocity comparison */
  const velocityData = wellsShort.map(w => ({
    name: w.short,
    color: w.color,
    avg_velocity: w.avg_velocity ?? 0,
    high_v: w.high_velocity_warnings ?? 0,
    low_v: w.low_velocity_warnings ?? 0,
    optimal: Math.max(0, (w.total_cycles ?? 0) - (w.high_velocity_warnings ?? 0) - (w.low_velocity_warnings ?? 0)),
  }));

  /* GLR Proxy */
  const glrData = wellsShort.map(w => ({
    name: w.short,
    color: w.color,
    glr_proxy: w.avg_volume && w.avg_shutin 
      ? ((w.avg_volume / w.avg_shutin) * 1000).toFixed(2) * 1 
      : 0,
  }));

  const wellMetricTable = wellsShort.map(w => ({
    name: w.short,
    color: w.color,
    shutin: w.avg_shutin?.toFixed(1) ?? "-",
    shutin_ok: w.avg_shutin >= 30 && w.avg_shutin <= 300,
    velocity: w.avg_velocity?.toFixed(0) ?? "-",
    vel_ok: w.avg_velocity >= 350 && w.avg_velocity <= 1200,
    volume: w.avg_volume?.toFixed(4) ?? "-",
    arrival: w.arrival_rate?.toFixed(1) ?? "-",
    cycles: w.total_cycles ?? 0,
    glr: w.avg_volume && w.avg_shutin 
      ? ((w.avg_volume / w.avg_shutin) * 1000).toFixed(1) 
      : "-",
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <SectionHeader 
        label="Well Parameters: Side-by-Side Comparison" 
        color={C.teal} 
        sub="Shut-in, velocity, GLR and after-flow compared across all wells" 
      />

      {/* Shut-in duration per well */}
      <ChartCard 
        title="Shut-In Duration: All Wells" 
        subtitle="Time the well stays closed between cycles." 
        accentColor={C.blue} 
        height={240}
      >
        <ResponsiveContainer>
          <BarChart data={shutinData} barCategoryGap="30%">
            <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
            <YAxis tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} unit=" min" {...yTitle("Shut-In Duration (min)")} />
            <Tooltip content={<AITooltip fieldKey="TimeOffTotal" contextData={props => ({
              well_name: props.label, 
              avg_shutin: props.payload?.[0]?.value, 
              optimal_range: "30-300 min" 
            })} />} />
            <ReferenceLine y={300} stroke={C.red} strokeDasharray="4 2" label={{ value: "Max 300", fontFamily: MONO, fontSize: 9, fill: C.red }} />
            <ReferenceLine y={30} stroke={C.darkCyan} strokeDasharray="4 2" label={{ value: "Min 30", fontFamily: MONO, fontSize: 9, fill: C.darkCyan }} />
            <Bar dataKey="avg_shutin" name="Avg Shut-In (min)" radius={[4,4,0,0]}>
              {shutinData.map((w, i) => <Cell key={i} fill={w.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* After-flow / volume per well */}
      <ChartCard lensId="volume" 
        title="Avg Cycle Volume (MCF): After-Flow Production" 
        subtitle="Gas produced per cycle, higher = well is producing more gas per plunger trip" 
        accentColor={C.green} 
        height={240}
      >
        <ResponsiveContainer>
          <BarChart data={afterFlowData} barCategoryGap="30%">
            <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
            <YAxis tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...yTitle("Avg Volume (MCF)")} />
            <Tooltip content={<AITooltip fieldKey="VolCycleTotal" contextData={props => ({
              well_name: props.label, 
              cycle_volume_mcf: props.payload?.[0]?.value 
            })} />} />
            <Bar dataKey="after_flow" name="Avg Volume (MCF)" radius={[4,4,0,0]}>
              {afterFlowData.map((w, i) => <Cell key={i} fill={w.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Velocity per well */}
      <ChartCard 
        title="Plunger Velocity: All Wells Compared" 
        subtitle="ft/min: the speed the plunger rises." 
        accentColor={C.amber} 
        height={240}
      >
        <ResponsiveContainer>
          <ComposedChart data={velocityData} barCategoryGap="30%">
            <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
            <YAxis tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} unit=" ft/m" {...yTitle("Avg Velocity (ft/min)")} />
            <Tooltip content={<AITooltip fieldKey="ArrivalVelocity" contextData={props => ({
              well_name: props.label, 
              avg_velocity: props.payload?.[0]?.value, 
              optimal_min: 350, 
              optimal_max: 1200 
            })} />} />
            <ReferenceLine y={1200} stroke={C.darkCyan} strokeDasharray="4 2" />
            <ReferenceLine y={350} stroke={C.red} strokeDasharray="4 2" />
            <Bar dataKey="avg_velocity" name="Avg Velocity" radius={[4,4,0,0]}>
              {velocityData.map((w, i) => <Cell key={i} fill={w.color} />)}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* GLR Proxy */}
      <ChartCard 
        title="GLR Proxy (Gas-to-Lift Ratio)" 
        subtitle="Volume/Shut-In ratio, higher = well is more efficient at using gas to lift liquid" 
        accentColor={C.pink} 
        height={230}
      >
        <ResponsiveContainer>
          <BarChart data={glrData} barCategoryGap="35%">
            <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
            <YAxis tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...yTitle("GLR Proxy (Vol/Shut-In x 1000)")} />
            <Tooltip 
              content={<AITooltip fieldKey="GLR" 
                  contextData={props => ({
                    well_name: props.label,
                    glr_proxy: props.payload?.[0]?.value,
                    note: "Gas-Liquid Ratio proxy: volume/shut-in, higher is better for plunger lift efficiency"
                  })} />} 
            />
            <Bar dataKey="glr_proxy" name="GLR Proxy" radius={[4,4,0,0]}>
              {glrData.map((w, i) => <Cell key={i} fill={w.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Full comparison table */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "16px 22px 12px", borderBottom: `1px solid ${C.border}` }}>
          {Icons.table}
          <div style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text }}>All Wells: Key Parameters Table</div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: MONO, fontSize: 11 }}>
            <thead>
              <tr>
                {["Well", "Shut-In (min)", "Velocity (ft/min)", "Vel Zone", "Arrival %", "Avg Vol (MCF)", "GLR Proxy", "Cycles"].map(h => (
                  <th key={h} style={{ 
                    padding: "12px 16px", 
                    background: C.panel, 
                    color: C.muted, 
                    fontSize: 9, 
                    letterSpacing: 1, 
                    textTransform: "uppercase", 
                    textAlign: h === "Well" ? "left" : "right", 
                    borderBottom: `1px solid ${C.border}`, 
                    whiteSpace: "nowrap" 
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {wellMetricTable.map((w, i) => (
                <tr key={i} style={{ 
                  background: i % 2 === 0 ? "transparent" : "#ffffff05",
                  borderBottom: `1px solid ${C.border}22` 
                }}>
                  <td style={{ padding: "10px 16px", color: w.color, borderBottom: `1px solid ${C.border}`, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                    {Icons.well} {w.name}
                  </td>
                  <td style={{ padding: "10px 16px", color: w.shutin_ok ? C.teal : C.amber, textAlign: "right", borderBottom: `1px solid ${C.border}`, fontWeight: 700 }}>{w.shutin}</td>
                  <td style={{ padding: "10px 16px", color: w.vel_ok ? C.green : C.red, textAlign: "right", borderBottom: `1px solid ${C.border}`, fontWeight: 700 }}>{w.velocity}</td>
                  <td style={{ padding: "10px 16px", textAlign: "right", borderBottom: `1px solid ${C.border}` }}>
                    <VelZone v={parseFloat(w.velocity)} />
                  </td>
                  <td style={{ padding: "10px 16px", color: parseFloat(w.arrival) >= 80 ? C.green : parseFloat(w.arrival) >= 50 ? C.amber : C.red, textAlign: "right", borderBottom: `1px solid ${C.border}`, fontWeight: 700 }}>{w.arrival}%</td>
                  <td style={{ padding: "10px 16px", color: C.purple, textAlign: "right", borderBottom: `1px solid ${C.border}` }}>{w.volume}</td>
                  <td style={{ padding: "10px 16px", color: C.pink, textAlign: "right", borderBottom: `1px solid ${C.border}` }}>{w.glr}</td>
                  <td style={{ padding: "10px 16px", color: C.blue, textAlign: "right", borderBottom: `1px solid ${C.border}` }}>{w.cycles}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
/* Tab: PREDICTIONS*/
function PredictionsTab({ wellsShort, winBounds }) {
  const [n, setN] = useState(20);
  const [selectedWellIdx, setSelectedWellIdx] = useState(0);
  const [wellData, setWellData] = useState({});
  const [loadingWell, setLoadingWell] = useState(null);

  const selectedWell = wellsShort[selectedWellIdx];

  useEffect(() => {
    if (!selectedWell) return;
    const fullName = selectedWell.well_name
      ? selectedWell.well_name.split("/").pop()
      : selectedWell.short;
    const key = `${fullName}_${n}`;
    if (wellData[key]) return;

    setLoadingWell(key);
    axios.get(`${API}/portfolio/well-forecast/${encodeURIComponent(fullName)}?n=${n}`)
      .then(r => setWellData(prev => ({ ...prev, [key]: r.data })))
      .catch(() => setWellData(prev => ({ ...prev, [key]: null })))
      .finally(() => setLoadingWell(null));
  }, [selectedWell?.short, n]);

  const cacheKey = selectedWell
    ? `${selectedWell.well_name ? selectedWell.well_name.split("/").pop() : selectedWell.short}_${n}`
    : null;
  const data      = cacheKey ? wellData[cacheKey] : null;
  const isLoading = loadingWell === cacheKey;

  // the window only trims the ACTUAL history that is drawn - the forecast model itself is always fitted on the last 30 cycles
  const histWin = data ? data.historical.map(h => ({ ...h, _t: parseT(h.time) }))
    .filter(h => !winBounds || h._t == null || (h._t >= winBounds.lo && h._t <= winBounds.hi)) : [];
  const combined = data ? [
    ...histWin.map(h => ({ index: Number(h.index), time: h.time, _t: h._t, actual: h.actual })),
    ...data.forecast.map(f => ({
      index: Number(f.index),
      time: f.time,
      _t: parseT(f.time),
      forecast: f.forecast,
      upper_80: f.upper_80,
      lower_80: f.lower_80,
    })),
  ] : [];
  const hasT = hasStamps(combined);
  const timeByIdx = new Map(combined.filter(d => d.time).map(d => [d.index, d.time]));

  const lastActual     = data?.historical[data.historical.length - 1]?.actual;
  const firstForecast  = data?.forecast[0]?.forecast;
  const forecastChange = lastActual && firstForecast
    ? (((firstForecast - lastActual) / lastActual) * 100).toFixed(1)
    : null;

  const DIRECTION_COLOR = { increasing: C.green, decreasing: C.red, stable: C.teal };
  const trendColor      = DIRECTION_COLOR[data?.direction] || C.muted;
  const prodCol         = data?.production_column || "FlowRateAtClose";

  const ww           = selectedWell || {};
  const health       = computeHealth(ww);
  const healthColor  = health >= 75 ? C.green : health >= 50 ? C.amber : C.red;
  const velocityOk   = (ww.avg_velocity ?? 0) >= 350 && (ww.avg_velocity ?? 0) <= 1200;
  const arrived      = (ww.arrival_rate ?? 0) >= 80;
  const yesRate      = ww.arrival_rate ?? 0;
  const noRate       = 100 - yesRate;
  const totalCycles  = ww.total_cycles || 1;
  const optimalCount = Math.max(
    0,
    (ww.total_cycles ?? 0) - (ww.high_velocity_warnings ?? 0) - (ww.low_velocity_warnings ?? 0)
  );
  const velocityZones = [
    { label: "Optimal (350, 1200 ft/min)", count: optimalCount,                    color: C.green },
    { label: "Too Fast (>1200 ft/min)",   count: ww.high_velocity_warnings ?? 0, color: C.amber },
    { label: "Too Slow (<350 ft/min)",    count: ww.low_velocity_warnings  ?? 0, color: C.red   },
  ];

  // Table helpers
  const getEfficiency = (w) => {
    const tc  = w.total_cycles || 1;
    const opt = Math.max(0, tc - (w.high_velocity_warnings ?? 0) - (w.low_velocity_warnings ?? 0));
    return ((opt / tc) * 100);
  };

  const getStatusInfo = (w) => {
    const h   = computeHealth(w);
    const vOk = (w.avg_velocity ?? 0) >= 350 && (w.avg_velocity ?? 0) <= 1200;
    const arr = (w.arrival_rate ?? 0) >= 80;
    if (h >= 75 && vOk && arr) return { label: "Healthy",  color: C.green };
    if (h >= 50 || arr)        return { label: "Warning",  color: C.amber };
    return                            { label: "Critical", color: C.red   };
  };

  const getVelocityZone = (v) => {
    if (v >= 350 && v <= 1200) return { label: "Optimal",  color: C.green };
    if (v > 1200)              return { label: "Too Fast", color: C.amber };
    return                            { label: "Too Slow", color: C.red   };
  };
  const rankedWells = [...wellsShort]
    .map((w, i) => ({ w, i, h: computeHealth(w) }))
    .sort((a, b) => b.h - a.h);
  const rankMap = {};
  rankedWells.forEach((item, pos) => { rankMap[item.w.short] = pos + 1; });
  const ThinBar = ({ pct, color }) => (
    <div style={{
      width: "100%", height: 3,
      background: `${color}22`,
      borderRadius: 2, overflow: "hidden", marginTop: 3,
    }}>
      <div style={{
        height: "100%",
        width: `${Math.min(Math.max(pct, 0), 100)}%`,
        background: color,
        borderRadius: 2,
        transition: "width 0.6s ease",
      }} />
    </div>
  );
  const StatusPill = ({ label, color }) => (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      fontFamily: MONO, fontSize: 10, color,
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: "50%",
        background: color, flexShrink: 0,
      }} />
      {label}
    </span>
  );

  const COLS = [
    { key: "well",     label: "Well",              align: "left"   },
    { key: "status",   label: "Status",            align: "left"   },
    { key: "health",   label: "Health",            align: "right"  },
    { key: "velocity", label: "Avg velocity",      align: "right"  },
    { key: "vzone",    label: "Velocity zone",     align: "left"   },
    { key: "eff",      label: "Cycle efficiency",  align: "right"  },
    { key: "arrival",  label: "Plunger arrival",   align: "right"  },
    { key: "cycles",   label: "Cycles",            align: "right"  },
    { key: "volume",   label: "Avg vol (MCF)",     align: "right"  },
    { key: "shutin",   label: "Shut-in (min)",     align: "right"  },
    { key: "rank",     label: "Rank",              align: "center" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <SectionHeader
        label="Per-Well Production Forecast"
        color={C.cyan}
        sub="Historical + AI Model forecast, 80% confidence band, per well"
      />

      {/* WELL SELECTOR TABS */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginRight: 4 }}>SELECT WELL:</span>
        {wellsShort.map((w, idx) => (
          <button
            key={w.short}
            onClick={() => setSelectedWellIdx(idx)}
            style={{
              padding: "8px 16px", borderRadius: 8, cursor: "pointer",
              border: `1px solid ${selectedWellIdx === idx ? w.color : C.border2}`,
              background: selectedWellIdx === idx ? `${w.color}18` : C.panel,
              color: selectedWellIdx === idx ? w.color : C.muted,
              fontFamily: MONO, fontSize: 10, fontWeight: 700,
              display: "flex", alignItems: "center", gap: 6,
              transition: "all 0.2s",
            }}
          >
            <span style={{ color: selectedWellIdx === idx ? w.color : C.muted }}>{Icons.well}</span>
            {w.short}
          </button>
        ))}
      </div>

      {/* SELECTED WELL SNAPSHOT CARD*/}
      <div style={{
        background: C.card,
        border: `1px solid ${selectedWell?.color}33`,
        borderLeft: `4px solid ${selectedWell?.color || C.cyan}`,
        borderRadius: 14, padding: "20px 22px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <span style={{ color: selectedWell?.color }}>{Icons.well}</span>
          <div style={{ fontFamily: SANS, fontWeight: 800, fontSize: 16, color: selectedWell?.color }}>
            {selectedWell?.short}
          </div>
          <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>: Prediction Snapshot</div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
              {[
                { label: "Velocity",        value: `${(ww.avg_velocity ?? 0).toFixed(0)} ft/min`, color: velocityOk ? C.green : C.red  },
                { label: "Arrival",         value: `${yesRate.toFixed(1)}%`,                       color: arrived    ? C.green : C.amber },
                { label: "Health",          value: `${health}/100`,                                color: healthColor                    },
                { label: "Plunger Arrived", value: arrived ? "YES" : "NO (avg)",                  color: arrived    ? C.green : C.red   },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>{label}</span>
                  <span style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, color }}>{value}</span>
                </div>
              ))}
            </div>
            <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginBottom: 8 }}>
              Plunger Arrival Status (Yes / No)
            </div>
            {[
              { label: `YES ${yesRate.toFixed(1)}%`, pct: yesRate, color: C.green },
              { label: `NO ${noRate.toFixed(1)}%`,   pct: noRate,  color: C.red   },
            ].map(({ label, pct, color }) => (
              <div key={label} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 6 }}>
                <div style={{ flex: 1, height: 14, background: C.border, borderRadius: 7, overflow: "hidden" }}>
                  <div style={{
                    height: "100%", width: `${pct}%`,
                    background: `linear-gradient(90deg, ${color}88, ${color})`,
                    borderRadius: 7, transition: "width 1.2s ease",
                  }} />
                </div>
                <span style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, color, minWidth: 72 }}>{label}</span>
              </div>
            ))}
            <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
              "Yes" = plunger successfully arrived at surface. "No" = failed arrival (liquid, low pressure, or mechanical).
            </div>
          </div>

          <div>
            <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginBottom: 10 }}>
              Velocity Zone Distribution
            </div>
            {velocityZones.map(({ label, count, color }) => {
              const pct = (count / totalCycles) * 100;
              return (
                <div key={label} style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontFamily: MONO, fontSize: 9, color }}>{label}</span>
                    <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, color }}>
                      {count} cycles ({pct.toFixed(0)}%)
                    </span>
                  </div>
                  <div style={{ height: 8, background: C.border, borderRadius: 4 }}>
                    <div style={{
                      height: "100%", width: `${pct}%`,
                      background: color, borderRadius: 4,
                      transition: "width 1s ease",
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* FORECAST CYCLE SELECTOR*/}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>Forecast ahead:</span>
        {[10, 20, 30].map(v => (
          <button key={v} onClick={() => setN(v)} style={{
            fontFamily: MONO, fontSize: 10, padding: "4px 10px", borderRadius: 6,
            border: `1px solid ${n === v ? C.cyan + "66" : C.border}`,
            background: n === v ? `${C.cyan}15` : "transparent",
            color: n === v ? C.cyan : C.muted, cursor: "pointer",
          }}>
            {v} cycles
          </button>
        ))}
      </div>

      {/* LOADING*/}
      {isLoading && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "40vh" }}>
          <div style={{ fontFamily: MONO, fontSize: 12, color: C.muted }}>
            Loading forecast for {selectedWell?.short}…
          </div>
        </div>
      )}
      {!isLoading && cacheKey && wellData[cacheKey] === null && (
        <div style={{ fontFamily: MONO, fontSize: 12, color: C.red, padding: 24 }}>
          Could not load forecast for {selectedWell?.short}, check backend connection.
        </div>
      )}

      {/* CHART & STATS*/}
      {!isLoading && data && (
        <>
          <div style={{
            background: C.card,
            border: `1px solid ${selectedWell?.color}44`,
            borderLeft: `4px solid ${selectedWell?.color || C.cyan}`,
            borderRadius: 10, padding: "14px 20px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <div>
              <div style={{ fontFamily: SANS, fontWeight: 800, fontSize: 18, color: selectedWell?.color || C.cyan }}>
                {selectedWell?.short}
              </div>
              <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 3 }}>
                {data.total_cycles} historical cycles · forecasting {n} cycles ahead · column: {prodCol}
              </div>
            </div>
            <div style={{
              padding: "8px 16px", borderRadius: 8,
              background: `${trendColor}15`, border: `1px solid ${trendColor}44`,
            }}>
              <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, textTransform: "uppercase", letterSpacing: 1 }}>Trend</div>
              <div style={{ fontFamily: MONO, fontSize: 14, fontWeight: 700, color: trendColor, marginTop: 2 }}>
                {data.direction === "increasing" ? "↑ " : data.direction === "decreasing" ? "↓ " : "→ "}
                {data.direction?.toUpperCase()}
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 18 }}>
            <ChartCard multiWell={false}
              title={`Historical + Forecast, ${selectedWell?.short}`}
              subtitle="Teal = actual measured, cyan dashed = AI Model forecast, shaded = 80% confidence band"
              accentColor={C.cyan}
              height={300}
            >
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={combined} margin={{ top: 10, right: 20, left: 10, bottom: hasT ? 22 : 20 }}>
                  <defs>
                    <linearGradient id="forecastGradCA" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={C.cyan} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={C.cyan} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="index" tick={{ fill: C.muted, fontSize: 9, fontFamily: MONO }} tickLine={false} height={hasT ? 20 : undefined} />
                  {dateXAxis(combined, "index", 5)}
                  <YAxis tick={{ fill: C.muted, fontSize: 9, fontFamily: MONO }} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={{ background: C.card, border: `1px solid ${C.border2}`, borderRadius: 8, fontFamily: MONO, fontSize: 11 }}
                    labelStyle={{ color: C.text, fontWeight: 700 }}
                    labelFormatter={v => timeByIdx.get(v) ? `Cycle ${v} · ${timeByIdx.get(v)}` : `Cycle ${v}`}
                  />
                  <Legend wrapperStyle={{ fontFamily: MONO, fontSize: 10, paddingTop: 16 }} />
                  <Area type="monotone" dataKey="actual"   name="Actual"    stroke={C.teal} fill={`${C.teal}10`} strokeWidth={2} dot={false} connectNulls={false} />
                  <Area type="monotone" dataKey="upper_80" name="Upper 80%" stroke="none"   fill="url(#forecastGradCA)" dot={false} connectNulls={false} legendType="none" />
                  <Area type="monotone" dataKey="lower_80" name="Lower 80%" stroke="none"   fill={C.bg} dot={false} connectNulls={false} legendType="none" />
                  <Line type="monotone" dataKey="forecast" name="AI Model Forecast" stroke={C.cyan} strokeWidth={2} strokeDasharray="6 3" dot={false} connectNulls={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "18px 16px" }}>
                <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>Trend Direction</div>
                <div style={{ fontFamily: SANS, fontWeight: 800, fontSize: 22, color: trendColor, textTransform: "capitalize" }}>
                  {data.direction === "increasing" ? "↑" : data.direction === "decreasing" ? "↓" : "→"} {data.direction}
                </div>
                <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 4 }}>
                  Slope: {data.trend_slope >= 0 ? "+" : ""}{data.trend_slope} /cycle
                </div>
                {forecastChange && (
                  <div style={{ fontFamily: MONO, fontSize: 10, color: trendColor, marginTop: 6 }}>
                    {forecastChange >= 0 ? "▲" : "▼"} {Math.abs(forecastChange)}% projected change
                  </div>
                )}
              </div>

              {data.daily_rate_mcfd != null && (
                <div style={{ background: C.card, border: `1px solid ${C.border}`, borderTop: `3px solid ${C.cyan}`, borderRadius: 14, padding: "18px 16px" }}>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>Est. Daily Production Rate</div>
                  <div style={{ fontFamily: MONO, fontSize: 28, fontWeight: 700, color: C.cyan }}>{data.daily_rate_mcfd.toFixed(0)}</div>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: C.muted, marginTop: 2 }}>MCF / day</div>
                  <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginTop: 4 }}>Sum of per-cycle volumes × daily cycle count</div>
                </div>
              )}

              {[
                { label: "Forecast Mean",  value: data.forecast_summary?.mean,     dec: 2 },
                { label: "Forecast Min",   value: data.forecast_summary?.min,      dec: 2 },
                { label: "Forecast Max",   value: data.forecast_summary?.max,      dec: 2 },
                { label: "Cycles Ahead",   value: data.forecast_summary?.n_cycles, dec: 0 },
              ].map(({ label, value, dec }) => (
                <div key={label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px" }}>
                  <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>{label}</div>
                  <div style={{ fontFamily: MONO, fontSize: 18, fontWeight: 700, color: C.text }}>
                    {value != null ? value.toFixed(dec) : "-"}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 18px", display: "flex", gap: 28, flexWrap: "wrap" }}>
            {[
              { icon: "-",   color: C.teal,  label: "Teal solid = actual history",                        desc: "Real measured gas production from well, cycle by cycle." },
              { icon: "- -", color: C.cyan,  label: "Cyan dashed = AI Model forecast", desc: "Model predicts these values for future cycles based on learned patterns." },
              { icon: "░",   color: C.muted, label: "Shaded band = uncertainty",                          desc: "80% of future actual values should fall inside this band. Wider = less certainty." },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, minWidth: 200 }}>
                <span style={{ fontFamily: MONO, fontSize: 13, color: item.color, flexShrink: 0, marginTop: 1 }}>{item.icon}</span>
                <div>
                  <div style={{ fontFamily: MONO, fontSize: 10, color: item.color, fontWeight: 700 }}>{item.label}</div>
                  <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginTop: 2 }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ALL WELLS */}
      <div style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 14,
        padding: "20px 22px",
      }}>
        {/* Table title row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: C.cyan }}>{Icons.predict}</span>
            <span style={{ fontFamily: SANS, fontWeight: 700, fontSize: 13, color: C.text }}>
              All Wells: Comparative Insights
            </span>
          </div>
          <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>
            {wellsShort.length} wells · click any row to load forecast
          </span>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{
            width: "100%", 
            borderCollapse: "collapse",
            minWidth: 860, 
            tableLayout: "fixed",
          }}>
            {/* THEAD */}
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {COLS.map(col => (
                  <th key={col.key} style={{
                    fontFamily: MONO, 
                    fontSize: 9,
                    color: C.muted,
                    textTransform: "uppercase", 
                    letterSpacing: 0.8,
                    textAlign: col.align,
                    padding: "12px 12px 10px",
                    fontWeight: 500,
                  }}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>

            {/* TBODY */}
            <tbody>
              {wellsShort.map((w, idx) => {
                const h = computeHealth(w);
                const hColor = h >= 75 ? C.green : h >= 50 ? C.amber : C.red;
                const status = getStatusInfo(w);
                const vZone = getVelocityZone(w.avg_velocity ?? 0);
                const eff = getEfficiency(w);
                const effColor = eff >= 70 ? C.green : eff >= 40 ? C.amber : C.red;
                const arr = w.arrival_rate ?? 0;
                const arrColor = arr >= 80 ? C.green : arr >= 50 ? C.amber : C.red;
                const vol = w.avg_volume ?? 0;
                const shutin = w.avg_shutin ?? 0;
                const shutinOk = shutin >= 30 && shutin <= 300;
                const rank = rankMap[w.short] ?? idx + 1;
                const isActive = idx === selectedWellIdx;

                return (
                  <tr
                    key={w.short}
                    onClick={() => setSelectedWellIdx(idx)}
                    style={{
                      cursor: "pointer",
                      borderBottom: `1px solid ${C.border}22`,
                      background: isActive ? `${w.color}0c` : (idx % 2 === 0 ? "transparent" : "#ffffff05"),
                      borderLeft: isActive ? `3px solid ${w.color}` : `3px solid transparent`,
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={e => {
                      if (!isActive) e.currentTarget.style.background = `${C.border}18`;
                    }}
                    onMouseLeave={e => {
                      if (!isActive) e.currentTarget.style.background = idx % 2 === 0 ? "transparent" : "#ffffff05";
                    }}
                  >
                    {/* Well name */}
                    <td style={{ 
                      padding: "14px 12px", 
                      color: w.color, 
                      fontWeight: 700,
                      fontSize: 11   
                    }}>
                      {w.short}
                    </td>

                    {/* Status */}
                    <td style={{ padding: "14px 12px" }}>
                      <span style={{
                        fontFamily: MONO,
                        fontSize: 10,
                        fontWeight: 700,
                        color: status.color,
                        padding: "3px 10px",
                        borderRadius: 4,
                        background: `${status.color}15`,
                        border: `1px solid ${status.color}33`
                      }}>
                        {status.label}
                      </span>
                    </td>

                    {/* Health */}
                    <td style={{ padding: "14px 12px", textAlign: "right" }}>
                      <span style={{ 
                        fontFamily: MONO, 
                        fontSize: 12, 
                        fontWeight: 700, 
                        color: hColor 
                      }}>
                        {h}
                        <span style={{ fontSize: 9, color: C.muted, fontWeight: 400 }}>/100</span>
                      </span>
                    </td>

                    {/* Avg velocity */}
                    <td style={{ padding: "14px 12px", textAlign: "right", color: vZone.color }}>
                      {(w.avg_velocity ?? 0).toFixed(0)} 
                      <span style={{ fontSize: 9, color: C.muted }}> ft/min</span>
                    </td>

                    {/* Velocity zone */}
                    <td style={{ padding: "14px 12px" }}>
                      <span style={{
                        fontFamily: MONO,
                        fontSize: 10,
                        fontWeight: 700,
                        color: vZone.color,
                        padding: "3px 10px",
                        borderRadius: 4,
                        background: `${vZone.color}15`,
                        border: `1px solid ${vZone.color}33`
                      }}>
                        {vZone.label}
                      </span>
                    </td>

                    {/* Cycle efficiency */}
                    <td style={{ padding: "14px 12px", textAlign: "right", color: effColor }}>
                      {eff.toFixed(1)}%
                    </td>

                    {/* Plunger arrival */}
                    <td style={{ padding: "14px 12px", textAlign: "right", color: arrColor }}>
                      {arr.toFixed(1)}%
                    </td>

                    {/* Total cycles */}
                    <td style={{ padding: "14px 12px", textAlign: "right", color: C.text }}>
                      {w.total_cycles ?? "-"}
                    </td>

                    {/* Avg volume */}
                    <td style={{ padding: "14px 12px", textAlign: "right", color: C.text }}>
                      {vol > 0 ? vol.toFixed(2) : "-"}
                    </td>

                    {/* Avg shut-in */}
                    <td style={{ padding: "14px 12px", textAlign: "right", color: shutinOk ? C.teal : C.amber }}>
                      {shutin > 0 ? shutin.toFixed(0) : "-"}
                      {shutin > 0 && <span style={{ fontSize: 9, color: C.muted }}> min</span>}
                    </td>

                    {/* Rank */}
                    <td style={{ 
                      padding: "14px 12px", 
                      textAlign: "center",
                      fontFamily: MONO,
                      fontSize: 11,
                      fontWeight: 700,
                      color: rank === 1 ? C.amber : rank === wellsShort.length ? C.red : C.muted
                    }}>
                      #{rank}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer Legend */}
        <div style={{
          display: "flex", 
          gap: 24, 
          flexWrap: "wrap",
          marginTop: 18, 
          paddingTop: 14,
          borderTop: `1px solid ${C.border}22`,
          fontFamily: MONO,
          fontSize: 9,
          color: C.muted
        }}>
          <div>Healthy / Optimal</div>
          <div>Warning / Borderline</div>
          <div>Critical</div>
          <div>Shut-in in normal range (30, 300 min)</div>
        </div>
      </div>

    </div>
  );
}
/* TAB: FULL STATS TABLE */
function TableTab({ wellsShort }) {
  const { advice: aiAdvice, loading: aiLoading, fetchAdvice } = useAIAdvice();
  const healthData = wellsShort.map(w => ({ ...w, health: computeHealth(w) }));
  const sorted = [...healthData].sort((a, b) => b.health - a.health);

  const avgVel = wellsShort.length 
    ? wellsShort.reduce((s, w) => s + (w.avg_velocity ?? 0), 0) / wellsShort.length 
    : 0;

  const avgArr = wellsShort.length 
    ? wellsShort.reduce((s, w) => s + (w.arrival_rate ?? 0), 0) / wellsShort.length 
    : 0;

  const avgShut = wellsShort.length 
    ? wellsShort.reduce((s, w) => s + (w.avg_shutin ?? 0), 0) / wellsShort.length 
    : 0;

  const totalCyc = wellsShort.reduce((s, w) => s + (w.total_cycles ?? 0), 0);

  // Trigger AI advice once
  useEffect(() => {
    if (wellsShort.length) {
      fetchAdvice("ArrivalVelocity", avgArr, "%", null, {
        avg_velocity: avgVel,
        avg_arrival: avgArr,
        total_cycles: totalCyc
      });
    }
  }, [wellsShort.length, avgArr, avgVel, totalCyc, fetchAdvice]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <SectionHeader 
        label="Fleet Statistics Table" 
        color={C.blue} 
        sub={`${wellsShort.length} wells, ${totalCyc.toLocaleString()} total cycles, clean view without triage alerts`} 
      />

      {/* Simple KPI row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
        <KPICard label="Avg Arrival Rate" value={avgArr} suffix="%" color={avgArr >= 80 ? C.green : avgArr >= 50 ? C.amber : C.red} icon={Icons.gauge} sub="Model-based" delay={0} />
        <KPICard label="Avg Velocity" value={avgVel} suffix=" ft/min" color={avgVel >= 350 && avgVel <= 1200 ? C.green : C.amber} icon={Icons.velocity} sub="Fleet average" delay={0.05} />
        <KPICard label="Avg Shut-In" value={avgShut} suffix=" min" color={avgShut >= 30 && avgShut <= 300 ? C.teal : C.amber} icon={Icons.clock} delay={0.1} />
        <KPICard label="Total Cycles" value={totalCyc} dec={0} color={C.blue} icon={Icons.chart} delay={0.15} />
      </div>

      {/* Clean Stats Table */}
      <div style={{ 
        background: C.card, 
        border: `1px solid ${C.border}`, 
        borderRadius: 14, 
        overflow: "hidden" 
      }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: MONO, fontSize: 11 }}>
            <thead>
              <tr style={{ background: C.panel, borderBottom: `2px solid ${C.border}` }}>
                {["Well", "Cycles", "Avg Velocity", "Velocity Zone", "Arrival %", "Avg Shut-In", "Avg Vol (MCF)", "Health Score", "Status"].map((h, i) => (
                  <th 
                    key={i} 
                    style={{
                      padding: "12px 16px",
                      textAlign: h === "Well" ? "left" : "right",
                      color: C.muted,
                      fontSize: 9,
                      letterSpacing: 1,
                      textTransform: "uppercase",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      borderBottom: `1px solid ${C.border}`
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((w, i) => {
                const velZone = w.avg_velocity >= 350 && w.avg_velocity <= 1200 
                  ? { label: "Optimal", color: C.green }
                  : w.avg_velocity > 1200 
                  ? { label: "Too Fast", color: C.amber }
                  : { label: "Too Slow", color: C.red };

                const arrColor = (w.arrival_rate ?? 0) >= 80 ? C.green : (w.arrival_rate ?? 0) >= 50 ? C.amber : C.red;
                const healthColor = w.health >= 75 ? C.green : w.health >= 50 ? C.amber : C.red;

                return (
                  <tr 
                    key={i} 
                    style={{ 
                      background: i % 2 === 0 ? "transparent" : "#ffffff05",
                      borderBottom: `1px solid ${C.border}22`
                    }}
                  >
                    <td style={{ padding: "12px 16px", color: w.color, fontWeight: 700 }}>
                      {w.short}
                    </td>
                    <td style={{ padding: "12px 16px", color: C.teal, textAlign: "right" }}>
                      {w.total_cycles}
                    </td>
                    <td style={{ 
                      padding: "12px 16px", 
                      color: velZone.color, 
                      textAlign: "right", 
                      fontWeight: 700 
                    }}>
                      {w.avg_velocity?.toFixed(0) ?? "-"} ft/min
                    </td>
                    <td style={{ padding: "12px 16px", textAlign: "right" }}>
                      <span style={{ 
                        fontFamily: MONO, fontSize: 9, fontWeight: 700,
                        color: velZone.color 
                      }}>
                        {velZone.label}
                      </span>
                    </td>
                    <td style={{ 
                      padding: "12px 16px", 
                      color: arrColor, 
                      textAlign: "right", 
                      fontWeight: 700 
                    }}>
                      {w.arrival_rate?.toFixed(1) ?? "-"}%
                    </td>
                    <td style={{ 
                      padding: "12px 16px", 
                      color: (w.avg_shutin >= 30 && w.avg_shutin <= 300) ? C.teal : C.amber, 
                      textAlign: "right" 
                    }}>
                      {w.avg_shutin?.toFixed(0) ?? "-"} min
                    </td>
                    <td style={{ padding: "12px 16px", color: C.purple, textAlign: "right" }}>
                      {w.avg_volume?.toFixed(3) ?? "-"}
                    </td>
                    <td style={{ 
                      padding: "12px 16px", 
                      color: healthColor, 
                      textAlign: "right", 
                      fontWeight: 700 
                    }}>
                      {w.health}
                    </td>
                    <td style={{ padding: "12px 16px", textAlign: "right" }}>
                      <span style={{
                        fontFamily: MONO, fontSize: 9, fontWeight: 700,
                        color: healthColor,
                        background: `${healthColor}18`,
                        padding: "2px 8px",
                        borderRadius: 4
                      }}>
                        {w.health >= 75 ? "Healthy" : w.health >= 50 ? "Monitor" : "Critical"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* TAB: PHYSICS-BASED ANALYSIS */
function PhysicsTab({ wellsShort, winQs }) {
  const [physData, setPhysData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeRule, setActiveRule] = useState("overview");
  const { advice: aiAdvice, loading: aiLoading, fetchAdvice } = useAIAdvice();

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(withQs(`${API}/portfolio/physics`, winQs));
        setPhysData(r.data);
      } catch {}
      setLoading(false);
    })();
  }, [winQs]);

  useEffect(() => {
    if (!physData?.fleet) return;
    const f = physData.fleet;
    fetchAdvice("LoadFactor", f.fleet_lf_mean, "", null, {
      fleet_lf_pass_rate: f.fleet_lf_pass,
      fleet_cp_pass_rate: f.fleet_cp_pass,
      fleet_si_pass_rate: f.fleet_si_pass,
      fleet_vel_opt_pct: f.fleet_vel_opt,
      fleet_physics_score: f.fleet_physics_score,
    });
  }, [physData]);

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "40vh" }}>
      <div style={{ fontFamily: MONO, fontSize: 12, color: C.muted }}>Computing physics metrics…</div>
    </div>
  );

  if (!physData || !physData.wells?.length) return (
    <div style={{ fontFamily: MONO, fontSize: 12, color: C.muted, padding: 24 }}>
      No physics data available, check backend connection.
    </div>
  );

  const wells = physData.wells;
  const fleet = physData.fleet;
  let wellsWithColor = wells.map(w => {
    const match = wellsShort.find(ws => 
      ws.well_name === w.well_name || 
      ws.short === w.short
    );
    return { ...w, color: match?.color || C.cyan };
  });
  wellsWithColor = [...wellsWithColor].sort((a, b) => {
    const aIsWell1 = (a.short || a.well_name || "").includes("WELL_1") ? 0 : 1;
    const bIsWell1 = (b.short || b.well_name || "").includes("WELL_1") ? 0 : 1;
    return aIsWell1 - bIsWell1;
  });
  const scoreColor = (s) => s == null ? C.muted : s >= 75 ? C.green : s >= 50 ? C.darkCyan : C.pink;
  const passColor  = (p) => p == null ? C.muted : p >= 80 ? C.green : p >= 50 ? C.darkCyan : C.pink;
  const lfColor    = (v) => v == null ? C.muted : v < 0.3 ? C.green : v < 0.5 ? C.darkCyan : C.pink;
  const RULE_TABS = [
    { id: "overview",    label: "Overview",       icon: Icons.chart,   color: C.cyan   },
    { id: "loadfactor",  label: "Load Factor",    icon: Icons.droplet, color: C.amber  },
    { id: "casing",      label: "Casing Pressure",icon: Icons.pressure,color: C.blue   },
    { id: "shutin",      label: "Shut-In Time",   icon: Icons.clock,   color: C.teal   },
    { id: "glr",         label: "GLR Feasibility",icon: Icons.gauge,   color: C.purple },
    { id: "velocity",    label: "Velocity",       icon: Icons.velocity,color: C.green  },
    { id: "table",       label: "Stats Table",    icon: Icons.table,   color: C.muted  },
  ];
  const overviewData = wellsWithColor.map(w => ({
    name: w.short,
    color: w.color,
    score: w.physics_score,
    lf: w.lf_pass_rate,
    cp: w.cp_pass_rate,
    si: w.si_pass_rate,
    vel: w.vel_opt_pct,
  }));

  const lfData = wellsWithColor.map(w => ({
    name: w.short,
    color: w.color,
    lf_mean: w.lf_mean,
    lf_pass: w.lf_pass_rate,
    lf_fail: w.lf_fail_count,
    threshold: 0.5,
  }));

  const casingData = wellsWithColor.map(w => ({
    name: w.short,
    color: w.color,
    pc: w.cp_avg_pc,
    pl: w.cp_avg_pl,
    ratio: w.cp_ratio,
    pass: w.cp_pass_rate,
    min_req: w.cp_avg_pl != null ? +(w.cp_avg_pl * 1.5).toFixed(2) : null,
  }));

  const shutinData = wellsWithColor.map(w => ({
    name: w.short,
    color: w.color,
    si_mean: w.si_mean,
    ts_mean: w.ts_mean,
    ts_max: w.ts_max,
    si_pass: w.si_pass_rate,
  }));

  const glrData = wellsWithColor.map(w => ({
    name: w.short,
    color: w.color,
    actual: w.actual_glr,
    minimum: w.min_glr,
    margin: w.glr_margin,
    feasible: w.glr_feasible,
  })).filter(d => d.actual != null);

  const velData = wellsWithColor.map(w => ({
    name: w.short,
    color: w.color,
    vel: w.vel_mean,
    opt_pct: w.vel_opt_pct,
    hi: w.vel_hi_count,
    lo: w.vel_lo_count,
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <SectionHeader
        label="Physics-Based Comparative Analysis"
        color={C.cyan}
        sub={`Whitepaper equations applied per well, ${wells.length} wells, well depth: ${fleet.well_depth_ft?.toFixed(0) ?? "-"} ft`}
      />

      {/* Fleet physics KPI strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(148px, 1fr))", gap: 12 }}>
        {[
          { label: "Fleet Physics Score", value: fleet.fleet_physics_score, suffix: "%", color: scoreColor(fleet.fleet_physics_score), icon: Icons.physics, sub: "Avg across all rules" },
          { label: "Load Factor Pass", value: fleet.fleet_lf_pass, suffix: "%", color: passColor(fleet.fleet_lf_pass), icon: Icons.droplet, sub: "Lf < 0.5" },
          { label: "Casing Pressure Pass", value: fleet.fleet_cp_pass, suffix: "%", color: passColor(fleet.fleet_cp_pass), icon: Icons.pressure, sub: "Pc > Pl×1.5" },
          { label: "Shut-In Time Pass", value: fleet.fleet_si_pass, suffix: "%", color: passColor(fleet.fleet_si_pass), icon: Icons.clock, sub: "ts ≥ min" },
          { label: "Velocity Optimal", value: fleet.fleet_vel_opt, suffix: "%", color: passColor(fleet.fleet_vel_opt), icon: Icons.velocity, sub: "350, 1200 ft/min" },
          { label: "Max Unload Time", value: fleet.tm_max_min, dec: 1, suffix: " min", color: C.blue, icon: Icons.clock, sub: `D/500 = ${fleet.well_depth_ft?.toFixed(0)}ft/500`},
        ].map((kpi, i) => (
          <KPICard key={i} label={kpi.label} value={kpi.value} suffix={kpi.suffix ?? ""} dec={kpi.dec ?? 1}
            color={kpi.color} icon={kpi.icon} sub={kpi.sub} delay={i * 0.04} />
        ))}
      </div>

      {/* Rule sub-tabs */}
      <SubTabs tabs={RULE_TABS} active={activeRule} onChange={setActiveRule} />

      {/* OVERVIEW */}
      {activeRule === "overview" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Physics score comparison bar */}
          <ChartCard lensId="well-compare" 
            title="Physics Compliance Score: Per Well" 
            accentColor={C.cyan} 
            height={240}
          >
            <ResponsiveContainer>
              <BarChart data={overviewData} barCategoryGap="28%">
                <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
                <YAxis domain={[0, 100]} unit="%" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...yTitle("Compliance Score (%)")} />
                <Tooltip content={<AITooltip fieldKey="PhysicsScore" contextData={props => ({
                  well_name: props.label,
                  physics_score: props.payload?.find(p => p.dataKey === "score")?.value,
                  lf_pass: props.payload?.find(p => p.dataKey === "lf")?.value,
                  cp_pass: props.payload?.find(p => p.dataKey === "cp")?.value,
                  si_pass: props.payload?.find(p => p.dataKey === "si")?.value,
                  vel_opt: props.payload?.find(p => p.dataKey === "vel")?.value,
                })} />} />
                <ReferenceLine y={75} stroke={C.green} strokeDasharray="4 2" label={{ value: "75% target", fontFamily: MONO, fontSize: 8, fill: C.green }} />
                <ReferenceLine y={50} stroke={C.darkCyan} strokeDasharray="4 2" label={{ value: "50% warn", fontFamily: MONO, fontSize: 8, fill: C.darkCyan }} />
                <Bar dataKey="score" name="Physics Score %" radius={[4,4,0,0]}>
                  {overviewData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Multi-rule grouped bar */}
          <ChartCard lensId="well-compare" 
            title="Rule Compliance: All Rules per Well" 
            subtitle="Each bar group = one well, bars = Load Factor, Casing Pressure, Shut-In, Velocity pass rates (%)" 
            accentColor={C.teal} 
            height={270}
          >
            <ResponsiveContainer>
              <BarChart data={overviewData} barCategoryGap="20%" barGap={2}>
                <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
                <YAxis domain={[0, 100]} unit="%" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...yTitle("Pass Rate (%)")} />
                <Tooltip content={<AITooltip fieldKey="PhysicsRules" contextData={props => ({
                  well_name: props.label,
                  load_factor_pass: props.payload?.find(p => p.dataKey === "lf")?.value,
                  casing_pressure_pass: props.payload?.find(p => p.dataKey === "cp")?.value,
                  shutin_pass: props.payload?.find(p => p.dataKey === "si")?.value,
                  velocity_optimal: props.payload?.find(p => p.dataKey === "vel")?.value,
                })} />} />
                <Legend formatter={v => <span style={{ fontFamily: MONO, fontSize: 9, color: C.text }}>{v}</span>} />
                <Bar dataKey="lf" name="Load Factor" fill={C.darkCyan} radius={[3,3,0,0]} />
                <Bar dataKey="cp" name="Casing Pres." fill={C.blue} radius={[3,3,0,0]} />
                <Bar dataKey="si" name="Shut-In Tim" fill={C.teal} radius={[3,3,0,0]} />
                <Bar dataKey="vel" name="Velocity Band (500, 1200)" fill={C.green} radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Per-well physics score tiles */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 14 }}>
            {wellsWithColor.map(w => {
              const sc = w.physics_score;
              const col = scoreColor(sc);
              return (
                <div key={w.short} style={{
                  background: C.card, 
                  border: `1px solid ${col}33`, 
                  borderTop: `3px solid ${col}`,
                  borderRadius: 10, 
                  padding: "14px 16px",
                }}>
                  <div style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, color: w.color, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
                    {Icons.well} {w.short}
                  </div>
                  <div style={{ fontFamily: MONO, fontSize: 28, fontWeight: 700, color: col, lineHeight: 1 }}>
                    {sc != null ? `${sc}%` : "-"}
                  </div>
                  <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, marginTop: 4 }}>Rule Base Score</div>
                  <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
                    {[
                      ["Lf Pass", w.lf_pass_rate, C.amber],
                      ["CP Pass", w.cp_pass_rate, C.blue ],
                      ["SI Pass", w.si_pass_rate, C.teal ],
                      ["Vel Opt", w.vel_opt_pct, C.green],
                    ].map(([label, val, c]) => (
                      <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>{label}</span>
                        <span style={{ fontFamily: MONO, fontSize: 10, fontWeight: 700, color: passColor(val) }}>
                          {val != null ? `${val}%` : "-"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* LOAD FACTOR */}
      {activeRule === "loadfactor" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <ChartCard 
              title="Avg Load Factor per Well" 
              subtitle="Lower is better. Red line = 0.5 threshold" 
              accentColor={C.amber} 
              height={240}
            >
              <ResponsiveContainer>
                <BarChart data={lfData} barCategoryGap="30%">
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
                  <YAxis domain={[0, 1]} tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...yTitle("Avg Load Factor (Lf)")} />
                  <Tooltip content={<AITooltip fieldKey="LoadFactor" contextData={props => ({
                    well_name: props.label,
                    avg_load_factor: props.payload?.[0]?.value,
                    lf_pass_rate: lfData.find(d => d.name === props.label)?.lf_pass,
                  })} />} />
                  <ReferenceLine y={0.5} stroke={C.red} strokeDasharray="4 2" label={{ value: "Lf = 0.5 limit", fontFamily: MONO, fontSize: 8, fill: C.red }} />
                  <ReferenceLine y={0.3} stroke={C.green} strokeDasharray="4 2" label={{ value: "0.3 target", fontFamily: MONO, fontSize: 8, fill: C.green }} />
                  <Bar dataKey="lf_mean" name="Avg Load Factor" radius={[4,4,0,0]}>
                    {lfData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard 
              title="Load Factor Rule Pass Rate (%)" 
              subtitle="% of cycles, higher = more cycles with safe liquid loading" 
              accentColor={C.teal} 
              height={240}
            >
              <ResponsiveContainer>
                <BarChart data={lfData} barCategoryGap="30%">
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
                  <YAxis domain={[0, 100]} unit="%" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...yTitle("Pass Rate (%)")} />
                  <Tooltip content={<AITooltip fieldKey="LoadFactor" contextData={props => ({
                    well_name: props.label,
                    lf_pass_rate: props.payload?.[0]?.value,
                    lf_fail_count: lfData.find(d => d.name === props.label)?.lf_fail,
                  })} />} />
                  <ReferenceLine y={80} stroke={C.green} strokeDasharray="4 2" label={{ value: "80% target", fontFamily: MONO, fontSize: 8, fill: C.green }} />
                  <Bar dataKey="lf_pass" name="LF Pass Rate %" radius={[4,4,0,0]}>
                    {lfData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <ChartCard 
            title="Load Factor Violations: Cycle Count per Well" 
            subtitle="Number of cycles with Lf ≥ 0.5 (plunger non-arrival risk)" 
            accentColor={C.red} 
            height={210}
          >
            <ResponsiveContainer>
              <BarChart data={lfData} barCategoryGap="30%">
                <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
                <YAxis tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} unit=" cyc" {...yTitle("Violation Cycles (count)")} />
                <Tooltip content={<AITooltip fieldKey="LoadFactor" contextData={props => ({
                  well_name: props.label,
                  violation_cycles: props.payload?.[0]?.value,
                })} />} />
                <Bar dataKey="lf_fail" name="Violation Cycles" radius={[4,4,0,0]}>
                  {lfData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

        </div>
      )}

      {/* CASING PRESSURE */}
      {activeRule === "casing" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 20 }}>
            <ChartCard title="Avg Casing Pressure vs. Min Required" accentColor={C.blue} height={250}>
              <ResponsiveContainer>
                <ComposedChart data={casingData} barCategoryGap="28%">
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
                  <YAxis tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} unit=" psi" {...yTitle("Pressure (psi)")} />
                  <Tooltip content={<AITooltip fieldKey="CasingPressure" contextData={props => ({
                    well_name: props.label,
                    avg_pc: props.payload?.find(p => p.dataKey === "pc")?.value,
                    min_required: props.payload?.find(p => p.dataKey === "min_req")?.value,
                    pc_pl_ratio: casingData.find(d => d.name === props.label)?.ratio,
                  })} />} />
                  <Legend formatter={v => <span style={{ fontFamily: MONO, fontSize: 9, color: C.text }}>{v}</span>} />
                  <Bar dataKey="pc" name="Avg Pc (psi)" fill={C.blue} radius={[4,4,0,0]} />
                  <Bar dataKey="min_req" name="Min Req" fill={`${C.teal}55`} radius={[4,4,0,0]} />
                  <Line dataKey="pl" name="Avg Line Pres." stroke={C.amber} strokeWidth={2} dot={false} strokeDasharray="4 2" />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Pc/Pl Ratio per Well" accentColor={C.teal} height={250}>
              <ResponsiveContainer>
                <BarChart data={casingData} barCategoryGap="30%">
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
                  <YAxis tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...yTitle("Pc/Pl Ratio")} />
                  <Tooltip content={<AITooltip fieldKey="CasingPressure" contextData={props => ({
                    well_name: props.label,
                    pc_pl_ratio: props.payload?.[0]?.value,
                  })} />} />
                  <ReferenceLine y={1.5} stroke={C.green} strokeDasharray="4 2" label={{ value: "1.5× req'd", fontFamily: MONO, fontSize: 8, fill: C.green }} />
                  <ReferenceLine y={1.2} stroke={C.amber} strokeDasharray="4 2" label={{ value: "1.2× warn", fontFamily: MONO, fontSize: 8, fill: C.amber }} />
                  <Bar dataKey="ratio" name="Pc/Pl Ratio" radius={[4,4,0,0]}>
                    {casingData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <ChartCard title="Casing Pressure Rule Pass Rate (%)" subtitle="% of cycles meeting Pc > Pl×1.5, optimal is better" accentColor={C.cyan} height={210}>
            <ResponsiveContainer>
              <BarChart data={casingData} barCategoryGap="30%">
                <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
                <YAxis domain={[0, 100]} unit="%" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...yTitle("Pass Rate (%)")} />
                <Tooltip content={<AITooltip fieldKey="CasingPressure" contextData={props => ({
                  well_name: props.label,
                  pass_rate: props.payload?.[0]?.value,
                })} />} />
                <ReferenceLine y={80} stroke={C.green} strokeDasharray="4 2" label={{ value: "80% target", fontFamily: MONO, fontSize: 8, fill: C.green }} />
                <Bar dataKey="pass" name="Pass Rate %" radius={[4,4,0,0]}>
                  {casingData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}

      {/* SHUT-IN TIME */}
      {activeRule === "shutin" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <ChartCard title="Avg Actual Shut-In vs. Min Required (ts)" accentColor={C.teal} height={250}>
              <ResponsiveContainer>
                <ComposedChart data={shutinData} barCategoryGap="28%">
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
                  <YAxis tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} unit=" min" {...yTitle("Shut-In Time (min)")} />
                  <Tooltip content={<AITooltip fieldKey="ShutInTime" contextData={props => ({
                    well_name: props.label,
                    avg_shutin: props.payload?.find(p => p.dataKey === "si_mean")?.value,
                    min_required: props.payload?.find(p => p.dataKey === "ts_mean")?.value,
                  })} />} />
                  <Legend formatter={v => <span style={{ fontFamily: MONO, fontSize: 9, color: C.text }}>{v}</span>} />
                  <Bar dataKey="si_mean" name="Avg Shut-In (min)" fill={C.teal} radius={[4,4,0,0]} />
                  <Bar dataKey="ts_mean" name="Min Required (min)" fill={`${C.darkCyan}55`} radius={[4,4,0,0]} />
                  <Line dataKey="ts_max" name="Max ts Variation" stroke={C.purple} strokeWidth={1.5} dot={false} strokeDasharray="3 2" />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Shut-In Rule Pass Rate (%)" accentColor={C.green} height={250}>
              <ResponsiveContainer>
                <BarChart data={shutinData} barCategoryGap="30%">
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
                  <YAxis domain={[0, 100]} unit="%" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...yTitle("Pass Rate (%)")} />
                  <Tooltip content={<AITooltip fieldKey="ShutInTime" contextData={props => ({
                    well_name: props.label,
                    pass_rate: props.payload?.[0]?.value,
                  })} />} />
                  <Bar dataKey="si_pass" name="Pass Rate %" radius={[4,4,0,0]}>
                    {shutinData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </div>
      )}

      {/* GLR FEASIBILITY */}
      {activeRule === "glr" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {glrData.length > 0 ? (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 20 }}>
                <ChartCard 
                  title="Actual vs. Minimum GLR per Well (scf/bbl)" 
                  subtitle="Purple = actual avg GLR, faded = min required" 
                  accentColor={C.purple} 
                  height={250}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart 
                      data={glrData} 
                      barCategoryGap="28%"
                      margin={{ top: 20, right: 20, left: 0, bottom: 10 }}
                    >
                      <CartesianGrid stroke={C.border} strokeDasharray="3 3" />

                      <XAxis 
                        dataKey="name" 
                        tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }}
                        {...xTitle("Well")}
                      />

                      {/* Left Y-Axis */}
                      <YAxis 
                        yAxisId="left"
                        width={112}
                        tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }}
                        unit=" scf/bbl"
                        label={{ 
                          value: 'Actual GLR', 
                          angle: -90, 
                          position: 'insideLeft',
                          style: { textAnchor: 'middle', fill: C.purple, fontSize: 10 }
                        }}
                      />

                      {/* Right Y-Axis */}
                      <YAxis 
                        yAxisId="right"
                        orientation="right"
                        width={112}
                        tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }}
                        unit=" scf/bbl"
                        label={{ 
                          value: 'Min Required', 
                          angle: 90, 
                          position: 'insideRight',
                          offset: 0,
                          style: { textAnchor: 'middle', fill: C.cyan, fontSize: 10, fontWeight: 700 }
                        }}
                      />

                      <Tooltip 
                        content={<AITooltip fieldKey="GLR" highContrast={true}
                            contextData={props => ({
                              well_name: props.label,
                              actual_glr: props.payload?.find(p => p.dataKey === "actual")?.value,
                              min_required: props.payload?.find(p => p.dataKey === "minimum")?.value,
                            })} />} 
                      />

                      <Legend 
                        formatter={v => (
                          <span style={{ fontFamily: MONO, fontSize: 9, color: C.text }}>
                            {v}
                          </span>
                        )} 
                      />

                      {/* Actual GLR */}
                      <Bar 
                        yAxisId="left"
                        dataKey="actual" 
                        name="Actual GLR (scf/bbl)" 
                        fill={C.purple} 
                        radius={[4, 4, 0, 0]} 
                      />

                      {/* Min Required */}
                      <Bar 
                        yAxisId="right"
                        dataKey="minimum" 
                        name="Min Required (scf/bbl)" 
                        fill={C.cyan} 
                        radius={[4, 4, 0, 0]} 
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="GLR Margin vs. Threshold (%)" subtitle="Positive = above min GLR (feasible). Negative = below threshold." accentColor={C.teal} height={250}>
                  <ResponsiveContainer>
                    <BarChart data={glrData} barCategoryGap="30%">
                      <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
                      <YAxis unit="%" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...yTitle("GLR Margin (%)")} />
                      <Tooltip content={<AITooltip fieldKey="GLR" contextData={props => ({
                        well_name: props.label,
                        glr_margin: props.payload?.[0]?.value,
                      })} />} />
                      <ReferenceLine y={0} stroke={C.red} strokeWidth={1.5} />
                      <ReferenceLine y={50} stroke={C.green} strokeDasharray="4 2" label={{ value: "+50% healthy", fontFamily: MONO, fontSize: 8, fill: C.green }} />
                      <Bar dataKey="margin" name="GLR Margin %" radius={[4,4,0,0]}>
                        {glrData.map((d, i) => <Cell key={i} fill={d.color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
                {glrData.map(w => {
                  const c = w.feasible ? (w.margin > 50 ? C.green : C.teal) : w.margin > -20 ? C.amber : C.red;
                  const label = w.feasible ? (w.margin > 50 ? "FEASIBLE" : "MARGINAL") : w.margin > -20 ? "BORDERLINE" : "NOT FEASIBLE";
                  return (
                    <div key={w.name} style={{ background: C.card, border: `1px solid ${c}44`, borderTop: `3px solid ${c}`, borderRadius: 10, padding: "14px 16px" }}>
                      <div style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, color: c, marginBottom: 8 }}>{w.name}</div>
                      <div style={{ fontFamily: MONO, fontSize: 9, color: c, background: `${c}15`, borderRadius: 4, padding: "3px 8px", display: "inline-block", marginBottom: 8 }}>{label}</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>Actual GLR</span>
                          <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, color: C.text }}>{w.actual?.toFixed(0) ?? "-"} scf/bbl</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>Min Required</span>
                          <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, color: C.muted }}>{w.minimum?.toFixed(0) ?? "-"} scf/bbl</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>Margin</span>
                          <span style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, color: c }}>{w.margin != null ? `${w.margin > 0 ? "+" : ""}${w.margin.toFixed(1)}%` : "-"}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 24, fontFamily: MONO, fontSize: 12, color: C.muted }}>
              GLR data unavailable, requires gas rate or flow rate column in the dataset.
            </div>
          )}
        </div>
      )}

      {/* VELOCITY */}
      {activeRule === "velocity" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <ChartCard title="Avg Arrival Velocity per Well (ft/min)" accentColor={C.green} height={250}>
              <ResponsiveContainer>
                <BarChart data={velData} barCategoryGap="30%">
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
                  <YAxis tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} unit=" ft/m" {...yTitle("Velocity (ft/min)")} />
                  <Tooltip content={<AITooltip fieldKey="ArrivalVelocity" contextData={props => ({
                    well_name: props.label,
                    avg_velocity: props.payload?.[0]?.value,
                    optimal_pct: velData.find(d => d.name === props.label)?.opt_pct,
                  })} />} />
                  <ReferenceLine y={1200} stroke={C.amber} strokeDasharray="4 2" label={{ value: "1200 max", fontFamily: MONO, fontSize: 8, fill: C.cyan }} />
                  <ReferenceLine y={500} stroke={C.green} strokeDasharray="4 2" label={{ value: "500 opt", fontFamily: MONO, fontSize: 8, fill: C.green }} />
                  <ReferenceLine y={350} stroke={C.red} strokeDasharray="4 2" label={{ value: "350 min", fontFamily: MONO, fontSize: 8, fill: C.darkCyan }} />
                  <Bar dataKey="vel" name="Avg Velocity (ft/min)" radius={[4,4,0,0]}>
                    {velData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Optimal Velocity Zone % per Well" accentColor={C.teal} height={250}>
              <ResponsiveContainer>
                <BarChart data={velData} barCategoryGap="30%">
                  <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
                  <YAxis domain={[0, 100]} unit="%" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...yTitle("Optimal Zone (%)")} />
                  <Tooltip content={<AITooltip fieldKey="ArrivalVelocity" contextData={props => ({
                    well_name: props.label,
                    optimal_velocity_pct: props.payload?.[0]?.value,
                  })} />} />
                  <Bar dataKey="opt_pct" name="Optimal %" radius={[4,4,0,0]}>
                    {velData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <ChartCard lensId="velocity-dist" title="Out-of-Zone Cycle Counts" subtitle="High velocity (>1200) and Low velocity (<350) cycle counts per well" accentColor={C.red} height={220}>
            <ResponsiveContainer>
              <BarChart data={velData} barCategoryGap="30%">
                <CartesianGrid stroke={C.border} strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} {...xTitle("Well")} />
                <YAxis tick={{ fontFamily: MONO, fontSize: 9, fill: C.muted }} unit=" cyc" {...yTitle("Cycle Count")} />
                <Tooltip content={<AITooltip fieldKey="ArrivalVelocity" contextData={props => ({
                  well_name: props.label,
                  high_vel_cycles: props.payload?.find(p => p.dataKey === "hi")?.value,
                  low_vel_cycles: props.payload?.find(p => p.dataKey === "lo")?.value,
                })} />} />
                <Legend formatter={v => <span style={{ fontFamily: MONO, fontSize: 9, color: C.text }}>{v}</span>} />
                <Bar dataKey="hi" name="High Vel (>1200)" fill={C.darkCyan} stackId="a" radius={[0,0,0,0]} />
                <Bar dataKey="lo" name="Low Vel (<350)" fill={C.pink} stackId="a" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}

      {/* STATS TABLE*/}
      {activeRule === "table" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ fontFamily: MONO, fontSize: 9, color: C.muted, lineHeight: 1.7 }}>
            Full per-well Rule base statistics table. All values computed from your actual data.
            Pass rates show % of cycles satisfying each rule base. Scores and rates in %.
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: MONO, fontSize: 10 }}>
              <thead>
                <tr style={{ background: C.panel, borderBottom: `2px solid ${C.border2}` }}>
                  {[
                    "Well", "Cycles", "Physics Score",
                    "LF Mean", "LF Pass%", "LF Fails",
                    "CP Pass%", "Pc Avg (psi)", "Pc/Pl",
                    "SI Mean (min)", "SI Pass%", "ts Min (min)",
                    "Vel Mean (ft/m)", "Vel Opt%",
                    "GLR Act.", "GLR Min", "GLR Margin%",
                    "LH Mean (ft)", "LV Mean (bbl)",
                  ].map(h => (
                    <th key={h} style={{ padding: "10px 12px", textAlign: "left", color: C.muted, fontWeight: 700, letterSpacing: "0.8px", fontSize: 9, textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {wellsWithColor.map((w, ri) => {
                  const sc = w.physics_score;
                  return (
                    <tr key={w.short} style={{ background: ri % 2 === 0 ? C.card : C.panel, borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: "10px 12px", color: w.color, fontWeight: 700, whiteSpace: "nowrap" }}>{w.short}</td>
                      <td style={{ padding: "10px 12px", color: C.text }}>{w.total_cycles}</td>
                      <td style={{ padding: "10px 12px", color: scoreColor(sc), fontWeight: 700 }}>{sc != null ? `${sc}%` : "-"}</td>
                      <td style={{ padding: "10px 12px", color: lfColor(w.lf_mean) }}>{w.lf_mean ?? "-"}</td>
                      <td style={{ padding: "10px 12px", color: passColor(w.lf_pass_rate) }}>{w.lf_pass_rate != null ? `${w.lf_pass_rate}%` : "-"}</td>
                      <td style={{ padding: "10px 12px", color: (w.lf_fail_count ?? 0) > 5 ? C.red : C.muted }}>{w.lf_fail_count ?? "-"}</td>
                      <td style={{ padding: "10px 12px", color: passColor(w.cp_pass_rate) }}>{w.cp_pass_rate != null ? `${w.cp_pass_rate}%` : "-"}</td>
                      <td style={{ padding: "10px 12px", color: C.text }}>{w.cp_avg_pc ?? "-"}</td>
                      <td style={{ padding: "10px 12px", color: (w.cp_ratio ?? 0) >= 1.5 ? C.green : (w.cp_ratio ?? 0) >= 1.2 ? C.amber : C.red }}>{w.cp_ratio ?? "-"}</td>
                      <td style={{ padding: "10px 12px", color: C.text }}>{w.si_mean ?? "-"}</td>
                      <td style={{ padding: "10px 12px", color: passColor(w.si_pass_rate) }}>{w.si_pass_rate != null ? `${w.si_pass_rate}%` : "-"}</td>
                      <td style={{ padding: "10px 12px", color: C.muted }}>{w.ts_mean ?? "-"}</td>
                      <td style={{ padding: "10px 12px", color: (w.vel_mean >= 350 && w.vel_mean <= 1200) ? C.green : C.red }}>{w.vel_mean ?? "-"}</td>
                      <td style={{ padding: "10px 12px", color: passColor(w.vel_opt_pct) }}>{w.vel_opt_pct != null ? `${w.vel_opt_pct}%` : "-"}</td>
                      <td style={{ padding: "10px 12px", color: C.text }}>{w.actual_glr?.toFixed(0) ?? "-"}</td>
                      <td style={{ padding: "10px 12px", color: C.muted }}>{w.min_glr?.toFixed(0) ?? "-"}</td>
                      <td style={{ padding: "10px 12px", color: (w.glr_margin ?? -999) > 0 ? C.green : C.red }}>{w.glr_margin != null ? `${w.glr_margin > 0 ? "+" : ""}${w.glr_margin.toFixed(1)}%` : "-"}</td>
                      <td style={{ padding: "10px 12px", color: C.text }}>{w.lh_mean ?? "-"}</td>
                      <td style={{ padding: "10px 12px", color: C.text }}>{w.lv_mean ?? "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* MAIN EXPORT*/
export default function ComparativeAnalysisPage() {
  const { isDark } = useTheme();
  applyTheme(getColors(isDark));
  const [wells,           setWells]          = useState([]);
  const [summary,         setSummary]        = useState(null);
  const [pressureData,    setPressureData]   = useState([]);
  const [pressureLoading, setPressureLoading] = useState(true);
  const [loading,         setLoading]        = useState(true);
  const [tab,             setTab]            = useState("overview");
  const { advice, loading: aiLoading, fetchAdvice } = useAIAdvice();

  /* DATA WINDOW: every cycle's open time (UNFILTERED) -> presets; the window is sent to the portfolio service as from/to/last */
  const [cycleTimes, setCycleTimes] = useState([]);
  useEffect(() => {
    axios.get(`${API}/portfolio/cycle-times`)
      .then(r => setCycleTimes((r.data?.cycles || []).map(c => ({ well: c.well, time: c.time, _t: parseT(c.time) }))))
      .catch(() => setCycleTimes([]));
  }, []);
  const tw = useTimeWindow(cycleTimes);
  const winQs = useMemo(() => benchWinQs(tw.win, tw.tMax), [tw.win, tw.tMax]);   // eslint-disable-line react-hooks/exhaustive-deps
  const winBounds = useMemo(() => {                                              // time span for trimming the Predictions history
    const { win, tMax } = tw;
    if (win.mode === "all" || win.mode === "cyc30" || tMax == null) return null;
    if (win.mode === "custom") return { lo: win.from ? parseT(`${win.from} 00:00`) : -Infinity, hi: win.to ? parseT(`${win.to} 23:59`) : Infinity };
    const pr = PRESETS.find(x => x.id === win.mode);
    return { lo: tMax - pr.ms, hi: tMax };
  }, [tw.win, tw.tMax]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(withQs(`${API}/portfolio/overview`, winQs));
        setWells(r.data.wells || []);
        setSummary(r.data.summary || {});
      } catch {}
      setLoading(false);
    })();
  }, [winQs]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(withQs(`${API}/portfolio/pressure`, winQs));
        setPressureData(r.data.wells || []);
      } catch {}
      setPressureLoading(false);
    })();
  }, [winQs]);

  useEffect(() => {
    if (!summary) return;
    fetchAdvice("ArrivalVelocity", summary.portfolio_arrival_rate, "%", null, {
      portfolio_arrival_rate: summary.portfolio_arrival_rate,
      total_cycles: summary.total_cycles,
      high_velocity_pct: summary.high_velocity_pct,
      low_velocity_pct: summary.low_velocity_pct,
    });
  }, [summary]);

  /* WELL_1 always first */
  const wellsShort = sortWells(
    wells.map((w, i) => ({
      ...w,
      short: shortName(w.well_name),
      color: WELL_COLORS[i % WELL_COLORS.length],
    }))
  );

  const totalCycles = summary?.total_cycles || 0;

  const TABS = [
    { id: "overview",    icon: Icons.chart,    label: "Overview",          color: C.cyan   },
    { id: "alerts",      icon: Icons.warning,  label: "Alerts & Alarms",   color: C.red    },
    { id: "performance", icon: Icons.velocity, label: "Performance",        color: C.amber  },
    { id: "pressure",    icon: Icons.pressure, label: "Pressure",           color: C.blue   },
    { id: "health",      icon: Icons.heart,    label: "Well Health",        color: C.green  },
    { id: "params",      icon: Icons.compare,  label: "Well Parameters",    color: C.teal   },
    { id: "predictions", icon: Icons.predict,  label: "Predictions",        color: C.purple },
    { id: "physics",     icon: Icons.physics,  label: "Physics Rules",      color: C.orange },
    { id: "table",       icon: Icons.table,    label: "Stats Table",         color: C.muted  },
  ];

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
      <div style={{ fontFamily: MONO, fontSize: 13, color: C.muted }}>Loading comparative analysis…</div>
    </div>
  );

  return (
    <div style={{ padding: "28px 24px 60px", maxWidth: 1400, margin: "0 auto" }}>
      <style>{`
        @keyframes fade-up { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        @keyframes pulse-ring { 0% { transform:scale(1); opacity:0.8; } 100% { transform:scale(2.5); opacity:0; } }
      `}</style>
      {/* Fleet KPI strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))", gap: 14, marginBottom: 32 }}>
        <KPICard label="Fleet Arrival Rate" value={summary?.portfolio_arrival_rate} suffix="%" color={C.green}  icon={Icons.gauge}    sub="Fleet avg, all wells"          delay={0}    />
        <KPICard label="Total Wells"         value={wellsShort.length}              dec={0}   color={C.cyan}   icon={Icons.well}     sub="Active monitored wells"          delay={0.04} />
        <KPICard label="Total Cycles"        value={totalCycles}                    dec={0}   color={C.blue}   icon={Icons.chart}    sub="Historical, all wells"          delay={0.08} />
        <KPICard label="High Velocity"        value={summary?.high_velocity_pct}   suffix="%" color={C.amber}  icon={Icons.velocity} sub="Avg % cycles > 1200 ft/min"     delay={0.12} />
        <KPICard label="Low Velocity"         value={summary?.low_velocity_pct}    suffix="%" color={C.red}    icon={Icons.velocity} sub="Avg % cycles < 350 ft/min"      delay={0.16} />
        <KPICard label="Avg Cycles/Well"     value={summary?.avg_cycles_per_well}  dec={0}   color={C.purple} icon={Icons.predict}  sub="Total cycles per well"           delay={0.20} />
      </div>

      {/* Data window (presets / custom range) */}
      {cycleTimes.some(c => c._t != null) && (
        <div style={{ marginBottom: 22 }}>
          <DataWindowBar ctl={tw} total={cycleTimes.length} isDark={isDark}
            note="Most Benchmarking charts compare wells (X-axis = well), so the window changes their numbers rather than adding a time axis; Predictions shows the timestamps. LAST 30 CYC = newest 30 cycles of each well." />
        </div>
      )}

      {/* Sub-tabs */}
      <SubTabs tabs={TABS} active={tab} onChange={setTab} />

      {/* Tab content */}
      {tab === "overview"     && <OverviewTab     wellsShort={wellsShort} summary={summary} totalCycles={totalCycles} aiLoading={aiLoading} advice={advice} />}
      {tab === "alerts"       && <AlertsTab       wellsShort={wellsShort} winQs={winQs} />}
      {tab === "performance"  && <PerformanceTab  wellsShort={wellsShort} summary={summary} aiLoading={aiLoading} advice={advice} />}
      {tab === "pressure"     && <PressureTab     wellsShort={wellsShort} pressureData={pressureData} pressureLoading={pressureLoading} />}
      {tab === "health"       && <HealthTab       wellsShort={wellsShort} />}
      {tab === "params"       && <WellParamsTab   wellsShort={wellsShort} />}
      {tab === "predictions"  && <PredictionsTab  wellsShort={wellsShort} winBounds={winBounds} />}
      {tab === "physics"      && <PhysicsTab      wellsShort={wellsShort} winQs={winQs} />}
      {tab === "table"        && <TableTab        wellsShort={wellsShort} />}
    </div>
  );
}